// Iter111 — Strategy Scanner V2 (trading-card redesign).
//
// What changed from the v1 UnifiedStrategyScanner:
//   * Auto-scans on page mount (no Run button).  Symbol source =
//     UNION of (saved per-user /api/watchlist) ∪ (Market Scanner
//     bridge from Iter109).
//   * Trading-card / sports-card pack layout per the
//     `/app/design_guidelines.json` blueprint.  PayoffChart is
//     hero-level (~180px) on every actionable card.
//   * "Top pick" gold badge — per symbol, the module with the
//     highest confidence_score gets a glowing ribbon.  Read-only
//     hint, NOT a recommendation to trade.
//   * "Copy Trade Ticket" button copies a broker-ready order
//     string to the clipboard.  Future-ready empty slot for a
//     "Send to Broker" button (not implemented yet).
//   * Watchlist Manager dialog — add / remove personal tickers
//     persisted via the EXISTING /api/watchlist CRUD.
//
// What stayed the same:
//   * Unified-preview backend aggregator — untouched.
//   * Five module evaluators / scanners — untouched.
//   * ARX WES parity engine — untouched.
//   * Market Scanner bridge (Iter109) — consumed as one of two
//     symbol sources.
//   * CompanyLogo + PayoffChart shared components (Iter108).
//
// Read-only invariants kept:
//   * NO trade execution.  NO broker order placement.  NO
//     journal write from the scanner.  NO MongoDB write from the
//     unified-preview backend.
//   * The watchlist endpoint IS write-capable (operator manages
//     their own watchlist) — that's the only persistence.
//
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw, Loader2, Star, Bookmark, Plus, X, Clipboard,
  ClipboardCheck, Send, Radar, SearchX, Activity, Pause, Play,
  BookmarkCheck, ChevronDown, Layers, Pin, PinOff, Check,
} from "lucide-react";
import { API } from "@/App";
import { CompanyLogo } from "@/components/CompanyLogo";
import { PayoffChart } from "@/components/PayoffChart";
import { useMarketScannerBridge } from "@/contexts/MarketScannerBridgeContext";
import { SkeletonRow } from "@/components/Skeleton";
import { WatchlistPicker, ManageWatchlistsModal, useActiveWatchlist } from "@/components/WatchlistPicker";
import { toast } from "sonner";

// Iter113.2 — Journal-context mirror chip.  Single read-only fetch
// on mount of the most recent Decision Journal entry (same shape as
// the Dashboard's outcome-pulse chip, Iter113.1) so journal context
// follows the operator from Dashboard → Scanner without a tab
// switch.  Click navigates to `/journal`.  Hidden when no entries
// exist.  Read-only — no new endpoint.
const JOURNAL_STATUS_COLORS = {
  ACCEPT: "#00FF88",
  CAUTION: "#FFB800",
  CAUTION_DATA_STALE: "#FFB800",
  REJECT: "#FF5577",
  BLOCKED: "#FF5577",
};

function relativeTimeShort(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const delta = Math.max(0, Date.now() - then);
  const m = Math.floor(delta / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// Iter112 — Live-scan prototype (Phase 7D).
// Behind a dev-only env flag (`REACT_APP_ENABLE_LIVE_SCAN=true`).
// When the flag is OFF the entire LIVE/PAUSED state machine is
// inert and the page renders identically to Iter111.
const LIVE_SCAN_ENABLED =
  String(process.env.REACT_APP_ENABLE_LIVE_SCAN || "")
    .toLowerCase() === "true";
const LIVE_CADENCE_MS = 60_000;            // 60-second cadence
const LIVE_MODE_IDLE = "IDLE";
const LIVE_MODE_LIVE = "LIVE";
const LIVE_MODE_PAUSED = "PAUSED";

// djb2 hash — used to short-circuit identical responses so React
// never re-renders when the rows[] payload is byte-equivalent
// to the previous live tick.  Pure, stable, no dependencies.
function liveScanResultHash(rows) {
  const json = JSON.stringify(rows || []);
  let h = 5381;
  for (let i = 0; i < json.length; i++) {
    h = ((h << 5) + h) + json.charCodeAt(i);
    h |= 0;
  }
  return String(h);
}

// Merge a fresh row list into the existing row list by stable key
// (`symbol|module_id`) so the React reconciler keeps the same DOM
// node for each card across live ticks.  This is the anti-flash
// + anti-reorder primitive: row order is FROZEN to the first scan
// (new keys append at end, missing keys keep their last value).
function mergeRowsByStableKey(prev, next) {
  if (!Array.isArray(next)) return prev || [];
  const prevList = Array.isArray(prev) ? prev : [];
  if (prevList.length === 0) return next.slice();
  const nextByKey = new Map();
  for (const r of next) {
    nextByKey.set(`${r.symbol}|${r.module_id}`, r);
  }
  const seen = new Set();
  const merged = prevList.map(r => {
    const key = `${r.symbol}|${r.module_id}`;
    seen.add(key);
    const updated = nextByKey.get(key);
    return updated || r;     // keep last value if missing this tick
  });
  // Append any genuinely new (symbol,module) pairs at the end.
  for (const r of next) {
    const key = `${r.symbol}|${r.module_id}`;
    if (!seen.has(key)) merged.push(r);
  }
  return merged;
}

// Status badge color palette.
const STATUS_THEME = {
  ENTER:           { fg: "#08080A", bg: "#00FF88", glow: "rgba(0,255,136,0.35)", label: "ENTER" },
  WATCH:           { fg: "#08080A", bg: "#FFB800", glow: "rgba(255,184,0,0.30)", label: "WATCH" },
  DEFEND:          { fg: "#08080A", bg: "#FF3366", glow: "rgba(255,51,102,0.30)", label: "DEFEND" },
  DEFENSE:         { fg: "#08080A", bg: "#FF3366", glow: "rgba(255,51,102,0.30)", label: "DEFENSE" },
  ASSIGNMENT_RISK: { fg: "#E5E5E7", bg: "#3F3F47", glow: "rgba(63,63,71,0.20)",  label: "RISK" },
  ALLOW_ASSIGNMENT:{ fg: "#08080A", bg: "#22D3FF", glow: "rgba(34,211,255,0.30)",label: "ALLOW" },
  NO_TRADE:        { fg: "#8A8A93", bg: "#1A1A1F", glow: "rgba(0,0,0,0)",        label: "NO TRADE" },
  BLOCKED:         { fg: "#FF3366", bg: "#3A1018", glow: "rgba(0,0,0,0)",        label: "BLOCKED" },
  CONTEXT_MISSING: { fg: "#FFB800", bg: "#2F2410", glow: "rgba(0,0,0,0)",        label: "CTX MISS" },
  ERROR:           { fg: "#FF3366", bg: "#26000C", glow: "rgba(0,0,0,0)",        label: "ERROR" },
};

const MODULE_LABELS = {
  BPCS: "Bull Put Credit Spread",
  BCCS: "Bear Call Credit Spread",
  IRON_CONDOR: "Iron Condor",
  CFM_CC_SHARES: "Covered Call · Shares",
  CFM_CC_LEAPS:  "Covered Call · LEAPS / PMCC",
  CFM_SHARES:    "Covered Call · Shares",
  CFM_LEAPS:     "Covered Call · LEAPS / PMCC",
};

const MODULE_VERB = {
  BPCS:           "Bull Put Credit Spread",
  BCCS:           "Bear Call Credit Spread",
  IRON_CONDOR:    "Iron Condor",
  CFM_CC_SHARES:  "Covered Call",
  CFM_CC_LEAPS:   "Diagonal Covered Call",
  CFM_SHARES:     "Covered Call",
  CFM_LEAPS:      "Diagonal Covered Call",
};

const ACTIONABLE_STATUSES = new Set(["ENTER", "WATCH", "ALLOW_ASSIGNMENT"]);

// ── Trade-ticket text formatter ────────────────────────────────────
function formatTradeTicket(symbol, moduleId, raw) {
  if (!raw) return "";
  const s = raw.selected_structure || {};
  const lines = [];
  lines.push(`ARXMENTIS · Strategy Scanner trade ticket`);
  lines.push(`Symbol: ${symbol}`);
  lines.push(`Strategy: ${MODULE_VERB[moduleId] || moduleId}`);
  if (s.expiry || raw.expiry) lines.push(`Expiration: ${s.expiry || raw.expiry}`);
  if (raw.dte != null) lines.push(`DTE: ${raw.dte} days`);
  // Legs
  const legs = Array.isArray(s.legs) ? s.legs : [];
  legs.forEach(l => {
    const action = String(l.action || "").toUpperCase();
    const t = String(l.type || "").toUpperCase();
    const verb = action === "SELL" ? "SELL TO OPEN" : "BUY TO OPEN";
    const strike = l.strike != null ? l.strike.toFixed?.(2) || l.strike : "?";
    lines.push(`  ${verb}: 1 × ${symbol} ${t} @ $${strike}`);
  });
  // CFM Shares: covered call from non-`legs[]` shape.
  if (legs.length === 0 && s.short_call) {
    const k = s.short_call.strike;
    lines.push(`  HOLD: 100 × ${symbol} shares`);
    lines.push(`  SELL TO OPEN: 1 × ${symbol} CALL @ $${k != null ? k.toFixed?.(2) || k : "?"}`);
  }
  if (s.net_credit != null)
    lines.push(`Net credit (target): $${Number(s.net_credit).toFixed(2)}`);
  if (s.call_credit != null && legs.length === 0)
    lines.push(`Call credit (target): $${Number(s.call_credit).toFixed(2)}`);
  if (s.max_profit != null) lines.push(`Max profit: $${Number(s.max_profit).toFixed(0)}`);
  if (s.max_loss != null) lines.push(`Max loss: $${Number(s.max_loss).toFixed(0)}`);
  if (s.breakeven != null) {
    const be = Array.isArray(s.breakeven)
      ? s.breakeven.map(x => `$${Number(x).toFixed(2)}`).join(" / ")
      : `$${Number(s.breakeven).toFixed(2)}`;
    lines.push(`Breakeven: ${be}`);
  }
  if (s.shares_breakeven != null) lines.push(`Shares breakeven: $${Number(s.shares_breakeven).toFixed(2)}`);
  if (s.roi != null) lines.push(`ROI: ${Number(s.roi).toFixed(1)}%`);
  if (s.ror != null) lines.push(`ROR: ${Number(s.ror).toFixed(1)}%`);
  // Exit plan
  if (s.exit_target_pct != null)
    lines.push(`Exit plan target: ${Number(s.exit_target_pct).toFixed(0)}% of credit`);
  if (s.stop_pct != null)
    lines.push(`Exit plan stop:   ${Number(s.stop_pct).toFixed(0)}% of credit`);
  lines.push("");
  lines.push("Read-only suggestion. Place at your broker manually.");
  return lines.join("\n");
}

// ── Sub-components ─────────────────────────────────────────────────
function StatusBadge({ status, big = false }) {
  const t = STATUS_THEME[status] || STATUS_THEME.NO_TRADE;
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center font-bold tracking-wider rounded-sm ${big ? "px-3 py-1 text-[11px]" : "px-2 py-0.5 text-[10px]"}`}
      style={{
        background: t.bg, color: t.fg,
        boxShadow: t.glow !== "rgba(0,0,0,0)" ? `0 0 12px ${t.glow}` : "none",
      }}
    >{t.label}</span>
  );
}

function DataChip({ label, value, accent = "#E5E5E7", testid }) {
  return (
    <div className="flex flex-col gap-0.5 px-2.5 py-1.5 rounded-sm bg-[#121216]/80 border border-[#26262B]" data-testid={testid}>
      <span className="text-[9px] font-mono uppercase tracking-[0.16em] text-[#5F5F66]">{label}</span>
      <span className="text-[12px] font-mono font-bold" style={{ color: accent }}>{value}</span>
    </div>
  );
}

function WesStrip({ wes, testid }) {
  if (!wes) return null;
  const d = wes.d || {};
  const h = wes.h || {};
  const fmt = v => v != null && Number.isFinite(Number(v)) ? Number(v).toFixed(2) : "—";
  const num = v => v != null && Number.isFinite(Number(v)) ? v : "—";
  return (
    <div className="rounded-sm border border-[#22D3FF]/15 bg-[#22D3FF]/5 px-3 py-2" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[8px] font-mono uppercase tracking-[0.24em] text-[#22D3FF] font-bold">WES</span>
        <span className="text-[8px] font-mono uppercase tracking-[0.18em] text-[#5F5F66]">D · H</span>
      </div>
      <div className="grid grid-cols-2 gap-1 text-[10px] font-mono text-[#8A8A93]">
        <div className="space-y-0.5">
          <div className="text-[8px] uppercase tracking-[0.16em] text-[#22D3FF]/60">D</div>
          <div>VAL ${fmt(d.val)} · POC ${fmt(d.poc)}</div>
          <div>VAH ${fmt(d.vah)} · ATR ${fmt(d.atr)} · {num(d.bars_used)}b</div>
        </div>
        <div className="space-y-0.5">
          <div className="text-[8px] uppercase tracking-[0.16em] text-[#22D3FF]/60">H</div>
          <div>VAL ${fmt(h.val)} · POC ${fmt(h.poc)}</div>
          <div>VAH ${fmt(h.vah)} · ATR ${fmt(h.atr)} · {num(h.bars_used)}b</div>
        </div>
      </div>
    </div>
  );
}

// ── Iter119.1 — ChainDrawer (per-card lazy option chain) ───────────
// Pure read-only drill-down. Mounts collapsed; on first expand it
// lazy-fetches /api/forge/candidates with the right option_type for
// the module (BPCS → put, BCCS/CFM → call, IC → both) and the
// operator's preferred σ band (persisted per-module so each
// drawer remembers its own settings across reloads). Re-fetches on
// band change.
//
// Strict invariants honoured (matches Iter119 doctrine):
//   • Read-only display; no broker / pin actions here.
//   • No journal mutation.
//   • Backend candidate selection is band-independent (proven by
//     pytest test_candidates_remain_put_side_regardless_of_option_type).
//
// Iter119.8 — Per-module persistence. Each drawer carries its own
// band / longLegMode / contracts preference under namespaced keys
// `forge.scannerChain.{moduleId}.{pref}`. optionType is already
// hardcoded per moduleId (see MODULE_OPTION_TYPE) and isn't user-
// toggleable, so it's trivially "persistent" — no key needed.
const SCANNER_PREF_PREFIX = "forge.scannerChain";

const MODULE_OPTION_TYPE = {
  BPCS:        "put",
  BCCS:        "call",
  IRON_CONDOR: "both",
  CFM_SHARES:  "call",
  CFM_LEAPS:   "call",
};

const isValidBand        = v => v === "2sigma" || v === "3sigma" || v === "full";
const isValidLongLegMode = v => v === "engine" || v === "wes"    || v === "manual";

function readScannerPref(moduleId, pref, fallback, validator) {
  try {
    const raw = localStorage.getItem(`${SCANNER_PREF_PREFIX}.${moduleId}.${pref}`);
    if (raw === null) return fallback;
    return validator(raw) ? raw : fallback;
  } catch { return fallback; }
}
function writeScannerPref(moduleId, pref, value) {
  try { localStorage.setItem(`${SCANNER_PREF_PREFIX}.${moduleId}.${pref}`, String(value)); }
  catch { /* ignore quota / disabled storage */ }
}
function readContractsPref(moduleId) {
  try {
    const raw = localStorage.getItem(`${SCANNER_PREF_PREFIX}.${moduleId}.contracts`);
    if (raw === null) return 1;
    const n = Math.floor(Number(raw));
    if (!Number.isFinite(n)) return 1;
    return Math.min(99, Math.max(1, n));
  } catch { return 1; }
}

const CHAIN_BAND_OPTIONS = [
  { id: "2sigma", label: "±2σ", title: "Trim chain to ±2σ — ~95% touch band (default)" },
  { id: "3sigma", label: "±3σ", title: "Trim chain to ±3σ — ~99% touch band" },
  { id: "full",   label: "FULL", title: "Show the full ITM/OTM band (no σ trim)" },
];

// Iter119.3 — Single-wing leg row block for the multi-leg spread
// comparison panel. Renders SHORT and LONG legs side-by-side for
// one wing (call wing for BCCS / Iron Condor call side; put wing
// for BPCS / Iron Condor put side). Picked column = engine's
// strikes; Pinned column = operator's hypothetical (matches engine
// for the un-pinned wing of an Iron Condor).
function SpreadLegBlock({ label, pickedShort, pickedLong,
                          pinnedShort, pinnedLong, pinnedSideMatch, fmtDollar }) {
  const cell = (row) => {
    if (!row) return "—";
    return `${fmtDollar(row.strike)} · b ${(row.bid ?? 0).toFixed(2)}`;
  };
  return (
    <div className="mb-2 rounded-sm bg-[#08080A] border border-[#26262B] px-2 py-1.5">
      <div className="text-[8px] font-bold uppercase tracking-[0.18em] text-[#8A8A93] mb-1">
        {label}
      </div>
      <div className="grid grid-cols-[1fr_1fr_1fr] gap-x-3 gap-y-0.5 text-[9px] font-mono">
        <div className="text-[#5F5F66]">Short</div>
        <div className="text-white">{cell(pickedShort)}</div>
        <div className={pinnedSideMatch ? "text-[#FFB800]" : "text-[#8A8A93]"}>
          {cell(pinnedShort)}
        </div>
        <div className="text-[#5F5F66]">Long</div>
        <div className="text-white">{cell(pickedLong)}</div>
        <div className={pinnedSideMatch ? "text-[#FFB800]" : "text-[#8A8A93]"}>
          {cell(pinnedLong)}
        </div>
      </div>
    </div>
  );
}

function ChainDrawer({ symbol, moduleId, structureStrikes, primaryLeg,
                       selectedStructure, spot, wes }) {
  const [open, setOpen] = useState(false);
  // Iter119.8 — Per-module persisted band. Initialized lazily from
  // localStorage so each module (BPCS / BCCS / IC / CFM_*) keeps
  // its own preferred σ window.
  const [band, setBandState] = useState(() => readScannerPref(moduleId, "band", "2sigma", isValidBand));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  // Iter119.2 — Strike pinned by the operator for side-by-side
  // comparison vs the engine's picked leg. Cleared when the drawer
  // is closed; persists across band changes so the operator can
  // widen ±3σ/FULL and still see the same pinned strike if it's
  // still in view.
  const [pinnedStrike, setPinnedStrike] = useState(null);
  // Iter119.4 — Operator-picked LONG leg, only consulted when the
  // long-leg pairing mode is "manual". In that mode the first click
  // pins the short, the second click pins the long; non-manual
  // modes (engine / WES) auto-compute the long and ignore this.
  const [pinnedLongStrike, setPinnedLongStrike] = useState(null);
  // Iter119.3 — Long-leg pairing mode for credit-spread modules.
  //   • "engine"  — match the engine's wing width (default).
  //   • "wes"     — anchor the long leg to the nearest VAL/POC/VAH
  //                 on the structurally-correct side. Falls back to
  //                 engine width if no WES level is on the right
  //                 side of the short leg.
  //   • "manual" (Iter119.4) — two-click flow: pin short, then pin
  //                 long. Unlocks asymmetric / non-WES wing widths.
  // Iter119.8 — Persisted per-module (operators tend to favour the
  // same pairing flow for the same module type).
  const [longLegMode, setLongLegModeState] = useState(() => readScannerPref(moduleId, "longLegMode", "engine", isValidLongLegMode));
  const cacheKey = useRef("");

  const optionType = MODULE_OPTION_TYPE[moduleId] || "put";
  const isMultiLeg = moduleId === "BPCS" || moduleId === "BCCS" || moduleId === "IRON_CONDOR";

  const setBand = useCallback((v) => {
    setBandState(v);
    writeScannerPref(moduleId, "band", v);
  }, [moduleId]);

  const setLongLegMode = useCallback((v) => {
    setLongLegModeState(v);
    writeScannerPref(moduleId, "longLegMode", v);
  }, [moduleId]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await axios.get(`${API}/forge/candidates`, {
        params: { symbol, option_type: optionType, strike_band: band, timeframe: "1h" },
      });
      setData(r.data);
      cacheKey.current = `${symbol}|${optionType}|${band}`;
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Chain fetch failed");
    } finally {
      setLoading(false);
    }
  }, [symbol, optionType, band]);

  // Lazy: only fetch when drawer is open. Re-fetch on band change
  // (key check avoids duplicate fetches on toggle close/reopen with
  // same band).
  useEffect(() => {
    if (!open) return;
    const k = `${symbol}|${optionType}|${band}`;
    if (cacheKey.current === k && data) return;
    load();
  }, [open, symbol, optionType, band, data, load]);

  // Clear pin when drawer is closed — feels cleaner than carrying
  // stale state across open/close cycles. Iter119.8 — contracts
  // NO LONGER resets here (it's now persisted per-module).
  useEffect(() => {
    if (!open) {
      setPinnedStrike(null);
      setPinnedLongStrike(null);
    }
  }, [open]);

  // Iter119.4 — Reset the manually-pinned long leg whenever the
  // pairing mode changes. Auto modes own the long leg; manual mode
  // starts fresh so the operator can't accidentally inherit a long
  // from a prior mode.
  useEffect(() => {
    setPinnedLongStrike(null);
  }, [longLegMode]);

  // Iter119.5 — Ephemeral confirmation for "Copy manual ticket".
  const [ticketCopied, setTicketCopied] = useState(false);

  // Iter119.6 — Contract-size stepper baked into the manual ticket
  // builder. Range 1..99 (anything bigger than two digits is a
  // hand-typed sweep, not a stepper sweep). Iter119.8 — persisted
  // per-module so an operator who scales 5-contract ICs habitually
  // doesn't have to re-set it every time.
  const [contracts, setContractsState] = useState(() => readContractsPref(moduleId));
  const clampContracts = (n) => {
    const x = Math.floor(Number(n));
    if (!Number.isFinite(x)) return 1;
    return Math.min(99, Math.max(1, x));
  };
  // Iter119.8 — Persist contracts on every change. Wraps the
  // setter; empty string (mid-typing) is allowed in state but
  // never persisted (we only write valid integer counts).
  const setContracts = useCallback((next) => {
    setContractsState(prev => {
      const v = typeof next === "function" ? next(prev) : next;
      if (v === "" || v == null) return v;
      const clamped = clampContracts(v);
      writeScannerPref(moduleId, "contracts", clamped);
      return clamped;
    });
  }, [moduleId]);

  // Highlight strikes that match this row's selected_structure legs
  // so the operator can spot the picked legs in the chain at a glance.
  const highlighted = useMemo(() => {
    const set = new Set();
    (structureStrikes || []).forEach(k => {
      if (k != null && Number.isFinite(Number(k))) set.add(Number(k));
    });
    return set;
  }, [structureStrikes]);

  const meta = data?.strike_band_meta || {};
  const chain = data?.chain || [];

  // Iter119.2 — Comparison rows. The engine's "picked" leg is looked
  // up in the same chain we already fetched, so all metrics come
  // from one source of truth (no extra API call). The "pinned" leg
  // is whatever strike the operator clicked. If the pinned strike
  // is not in the current band view (e.g. user widened then
  // narrowed), the pin survives but its row is null until the band
  // is widened again — we handle that with a small disclaimer.
  const findInChain = (target, type) => {
    if (target == null) return null;
    return chain.find(r =>
      Math.abs(Number(r.strike) - Number(target)) < 0.001
      && (!type || (r.type || "").toLowerCase() === type)
    ) || null;
  };
  const pickedRow = primaryLeg ? findInChain(primaryLeg.strike, primaryLeg.type) : null;
  const pinnedRow = pinnedStrike != null ? findInChain(pinnedStrike, optionType === "both" ? null : optionType) : null;

  // ── Iter119.3 — Multi-leg spread infrastructure ────────────────
  // Extract the engine's full set of legs from `selected_structure`
  // so we can build credit-spread P&L (max profit, max loss, R:R,
  // breakeven incl. long, credit-to-width %).
  const engineLegs = useMemo(() => {
    const s = selectedStructure || {};
    const fromObj = (l, type, role) => l ? { strike: Number(l.strike), type, role } : null;
    let sp = fromObj(s.short_put, "put", "SHORT_PUT");
    let lp = fromObj(s.long_put,  "put", "LONG_PUT");
    let sc = fromObj(s.short_call, "call", "SHORT_CALL");
    let lc = fromObj(s.long_call, "call", "LONG_CALL");
    if (Array.isArray(s.legs)) {
      s.legs.forEach(l => {
        const role = (l?.role || l?.side || "").toUpperCase();
        const t = (l?.type || l?.option_type || "").toLowerCase();
        const obj = { strike: Number(l?.strike), type: t === "call" ? "call" : "put", role };
        if (!sp && /SHORT.*PUT|PUT.*SHORT/.test(role) && obj.type === "put")  sp = obj;
        if (!lp && /LONG.*PUT|PUT.*LONG/.test(role)   && obj.type === "put")  lp = obj;
        if (!sc && /SHORT.*CALL|CALL.*SHORT/.test(role) && obj.type === "call") sc = obj;
        if (!lc && /LONG.*CALL|CALL.*LONG/.test(role)   && obj.type === "call") lc = obj;
      });
    }
    return { sp, lp, sc, lc };
  }, [selectedStructure]);

  // Engine wing widths per side. Used as the default auto-pair gap
  // when the operator pins a hypothetical short.
  const engineWidths = useMemo(() => {
    const w = { put: null, call: null };
    if (engineLegs.sp && engineLegs.lp) w.put  = Math.abs(engineLegs.sp.strike - engineLegs.lp.strike);
    if (engineLegs.sc && engineLegs.lc) w.call = Math.abs(engineLegs.lc.strike - engineLegs.sc.strike);
    return w;
  }, [engineLegs]);

  // WES levels — prefer daily (`d`) for credit-spread placement,
  // fall back to hourly (`h`) only if daily is missing.
  const wesLevels = useMemo(() => {
    const src = (wes?.d && (wes.d.val != null || wes.d.poc != null || wes.d.vah != null))
      ? wes.d
      : (wes?.h || {});
    const out = [];
    if (Number.isFinite(Number(src.val))) out.push({ key: "VAL", value: Number(src.val) });
    if (Number.isFinite(Number(src.poc))) out.push({ key: "POC", value: Number(src.poc) });
    if (Number.isFinite(Number(src.vah))) out.push({ key: "VAH", value: Number(src.vah) });
    return out;
  }, [wes]);

  // Auto-pair the long leg for the pinned short.
  //   • Manual mode (Iter119.4): use the operator-pinned long leg
  //     directly. If the operator hasn't pinned a long yet, return
  //     null so the comparison panel waits for the second click.
  //   • Put credit spreads: long is BELOW short. WES anchor =
  //     nearest WES level strictly BELOW the short. Engine width
  //     fallback uses (short − engineWidth.put).
  //   • Call credit spreads: long is ABOVE short. WES anchor =
  //     nearest WES level strictly ABOVE the short. Engine width
  //     fallback uses (short + engineWidth.call).
  // Returns { strike, source }, or null if no viable pair exists.
  const computeLongLeg = useCallback((shortStrike, type) => {
    if (shortStrike == null || !type) return null;

    // 0) Manual mode — operator-driven. No automatic fallback.
    if (longLegMode === "manual" && isMultiLeg) {
      if (pinnedLongStrike == null) return null;
      return { strike: pinnedLongStrike, source: "MANUAL", anchor: null, anchor_value: null };
    }

    const sameTypeRows = chain.filter(r => (r.type || "").toLowerCase() === type);
    if (sameTypeRows.length === 0) return null;

    // 1) WES anchor mode — only honoured for multi-leg modules.
    if (longLegMode === "wes" && wesLevels.length > 0 && isMultiLeg) {
      const isPut = type === "put";
      // Pick the WES level strictly on the long-side of the short.
      const candLevels = wesLevels.filter(l => isPut ? l.value < shortStrike : l.value > shortStrike);
      if (candLevels.length > 0) {
        // For puts: long is the highest such level that's still below short.
        // For calls: long is the lowest such level that's still above short.
        const target = isPut ? Math.max(...candLevels.map(l => l.value))
                              : Math.min(...candLevels.map(l => l.value));
        const winner = candLevels.find(l => l.value === target);
        // Find the chain strike closest to that target on the long-side.
        const eligible = sameTypeRows.filter(r => isPut ? r.strike <= target + 0.001 : r.strike >= target - 0.001);
        if (eligible.length > 0) {
          const best = eligible.reduce((b, r) =>
            !b || Math.abs(r.strike - target) < Math.abs(b.strike - target) ? r : b, null);
          return { strike: best.strike, source: "WES", anchor: winner?.key, anchor_value: target };
        }
      }
      // No WES level on the right side — silently fall through to engine width.
    }

    // 2) Engine-width fallback.
    const w = type === "put" ? engineWidths.put : engineWidths.call;
    if (w && Number.isFinite(w) && w > 0) {
      const target = type === "put" ? shortStrike - w : shortStrike + w;
      const best = sameTypeRows.reduce((b, r) =>
        !b || Math.abs(r.strike - target) < Math.abs(b.strike - target) ? r : b, null);
      if (best) return { strike: best.strike, source: "ENGINE_WIDTH", anchor: null, anchor_value: w };
    }
    return null;
  }, [chain, longLegMode, wesLevels, engineWidths, isMultiLeg, pinnedLongStrike]);

  // Iter119.4 — Row-click handler. Two-click flow for manual mode,
  // single-click toggle for engine / WES auto modes.
  const handleRowClick = useCallback((rowStrike) => {
    const rs = Number(rowStrike);
    if (longLegMode === "manual" && isMultiLeg) {
      if (pinnedStrike == null) {
        // Step 1: pin SHORT.
        setPinnedStrike(rs);
        setPinnedLongStrike(null);
        return;
      }
      if (Math.abs(pinnedStrike - rs) < 0.001) {
        // Re-click the short → clear everything.
        setPinnedStrike(null);
        setPinnedLongStrike(null);
        return;
      }
      if (pinnedLongStrike != null && Math.abs(pinnedLongStrike - rs) < 0.001) {
        // Re-click the long → clear just the long (back to step 2).
        setPinnedLongStrike(null);
        return;
      }
      // Step 2 (or replace long): pin LONG.
      setPinnedLongStrike(rs);
      return;
    }
    // Auto modes — simple toggle on a single short.
    setPinnedStrike(prev => prev != null && Math.abs(prev - rs) < 0.001 ? null : rs);
  }, [longLegMode, isMultiLeg, pinnedStrike, pinnedLongStrike]);

  // Resolve a chain row (for full Bid/Δ/IV/OI metrics) given a
  // strike + type. Mirrors findInChain but exposed as a stable
  // helper for the metrics functions below.
  const rowOf = useCallback((strike, type) =>
    findInChain(strike, type),
    // findInChain is defined inline above and only depends on `chain`;
    // listing it would force a redeclare per render. Pin to `chain`.
    [chain]);

  // Per-leg snapshot metrics (single sold leg).
  const legMetrics = (row) => {
    if (!row) return null;
    const bid = Number(row.bid || 0);
    const strike = Number(row.strike || 0);
    const isPut = (row.type || "").toLowerCase() === "put";
    const roiPct = strike > 0 ? (bid / strike) * 100 : null;
    const breakeven = isPut ? (strike - bid) : (strike + bid);
    const widthVsSpotPct = (spot && strike) ? ((strike - spot) / spot) * 100 : null;
    return {
      strike, bid, ask: Number(row.ask || 0),
      delta: row.delta != null ? Math.abs(Number(row.delta)) : null,
      iv: row.iv != null ? Number(row.iv) : null,
      oi: row.open_interest != null ? Number(row.open_interest) : null,
      roiPct, breakeven, widthVsSpotPct,
      type: row.type,
    };
  };

  // Full credit-spread metrics (sell short, buy long).
  //   credit = short.bid - long.ask   (worst-case fill, no mid-fudge)
  //   width  = |short.strike - long.strike|
  //   max_profit = credit * 100   (per contract)
  //   max_loss   = (width - credit) * 100, floored at 0
  //   breakeven  = short - credit (puts)  /  short + credit (calls)
  //   rr         = max_profit / max_loss  (uncapped — could be Infinity)
  //   credit_pct_width = credit / width   (a Tasty-style ROI gauge)
  const spreadMetrics = (shortRow, longRow) => {
    if (!shortRow || !longRow) return null;
    const sBid = Number(shortRow.bid || 0);
    const lAsk = Number(longRow.ask || 0);
    const isPut = (shortRow.type || "").toLowerCase() === "put";
    const credit = Math.max(sBid - lAsk, 0);    // negative-credit means engine-mis-pair → clamp
    const width = Math.abs(Number(shortRow.strike) - Number(longRow.strike));
    const max_profit = credit * 100;
    const max_loss   = Math.max(width - credit, 0) * 100;
    const be = isPut ? (shortRow.strike - credit) : (shortRow.strike + credit);
    const rr = max_loss > 0 ? max_profit / max_loss : null;
    const credit_pct_width = width > 0 ? (credit / width) * 100 : null;
    return { credit, width, max_profit, max_loss, breakeven: be, rr, credit_pct_width };
  };

  // Iron Condor combined metrics — both wings net out into ONE
  // payoff diagram, so max-loss is one-sided (not double).
  const icMetrics = (putShortRow, putLongRow, callShortRow, callLongRow) => {
    const putM = spreadMetrics(putShortRow, putLongRow);
    const callM = spreadMetrics(callShortRow, callLongRow);
    const credit = (putM?.credit || 0) + (callM?.credit || 0);
    const width = Math.max(putM?.width || 0, callM?.width || 0);
    const max_profit = credit * 100;
    const max_loss   = Math.max(width - credit, 0) * 100;
    const lower_be = putShortRow ? putShortRow.strike - credit : null;
    const upper_be = callShortRow ? callShortRow.strike + credit : null;
    const rr = max_loss > 0 ? max_profit / max_loss : null;
    const credit_pct_width = width > 0 ? (credit / width) * 100 : null;
    return { credit, width, max_profit, max_loss, lower_be, upper_be, rr,
             credit_pct_width, putWing: putM, callWing: callM };
  };

  // Resolve the "PICKED" snapshot — single leg for CFM, full spread
  // for credit modules.
  const pickedSpread = useMemo(() => {
    if (!isMultiLeg) return null;
    if (moduleId === "IRON_CONDOR") {
      return icMetrics(
        engineLegs.sp ? rowOf(engineLegs.sp.strike, "put")  : null,
        engineLegs.lp ? rowOf(engineLegs.lp.strike, "put")  : null,
        engineLegs.sc ? rowOf(engineLegs.sc.strike, "call") : null,
        engineLegs.lc ? rowOf(engineLegs.lc.strike, "call") : null,
      );
    }
    if (moduleId === "BPCS" && engineLegs.sp && engineLegs.lp) {
      return spreadMetrics(rowOf(engineLegs.sp.strike, "put"),
                            rowOf(engineLegs.lp.strike, "put"));
    }
    if (moduleId === "BCCS" && engineLegs.sc && engineLegs.lc) {
      return spreadMetrics(rowOf(engineLegs.sc.strike, "call"),
                            rowOf(engineLegs.lc.strike, "call"));
    }
    return null;
  }, [isMultiLeg, moduleId, engineLegs, rowOf]);

  // Resolve the "PINNED" snapshot — operator pins ONE strike (the
  // hypothetical short on whichever side the row sits on); we
  // auto-pair the long via computeLongLeg().
  const pinnedSpread = useMemo(() => {
    if (!isMultiLeg || !pinnedRow) return null;
    const pinType = (pinnedRow.type || "").toLowerCase();
    const longPair = computeLongLeg(pinnedRow.strike, pinType);
    const longRow = longPair ? rowOf(longPair.strike, pinType) : null;
    if (moduleId === "IRON_CONDOR") {
      // Keep the un-pinned wing from the engine; replace the pinned
      // side with the hypothetical short+auto-long.
      if (pinType === "put") {
        return {
          metrics: icMetrics(pinnedRow, longRow,
            engineLegs.sc ? rowOf(engineLegs.sc.strike, "call") : null,
            engineLegs.lc ? rowOf(engineLegs.lc.strike, "call") : null),
          pinnedSide: "put", autoLong: longPair, longRow,
          callShortRow: engineLegs.sc ? rowOf(engineLegs.sc.strike, "call") : null,
          callLongRow:  engineLegs.lc ? rowOf(engineLegs.lc.strike, "call") : null,
          putShortRow:  pinnedRow,
          putLongRow:   longRow,
        };
      }
      return {
        metrics: icMetrics(
          engineLegs.sp ? rowOf(engineLegs.sp.strike, "put") : null,
          engineLegs.lp ? rowOf(engineLegs.lp.strike, "put") : null,
          pinnedRow, longRow),
        pinnedSide: "call", autoLong: longPair, longRow,
        putShortRow:  engineLegs.sp ? rowOf(engineLegs.sp.strike, "put") : null,
        putLongRow:   engineLegs.lp ? rowOf(engineLegs.lp.strike, "put") : null,
        callShortRow: pinnedRow,
        callLongRow:  longRow,
      };
    }
    return {
      metrics: spreadMetrics(pinnedRow, longRow),
      pinnedSide: pinType, autoLong: longPair, longRow,
    };
  }, [isMultiLeg, moduleId, pinnedRow, computeLongLeg, engineLegs, rowOf]);

  // Single-leg fallback metrics (CFM modules). Same as Iter119.2.
  const pickedM = !isMultiLeg ? legMetrics(pickedRow) : null;
  const pinnedM = !isMultiLeg ? legMetrics(pinnedRow) : null;

  const compareOpen = isMultiLeg
    ? (pickedSpread != null || pinnedSpread?.metrics != null)
    : (pinnedRow != null && pickedRow != null);

  // Coloring helpers — for credit/sold legs (which is what every
  // scanner module produces) "better" generally means: higher bid,
  // lower |delta|, higher OI, higher ROI%. We tag each row with a
  // winner badge so the operator can read the comparison in one
  // glance without having to do mental math.
  const winner = (a, b, higherIsBetter = true) => {
    if (a == null || b == null) return null;
    if (Math.abs(a - b) < 1e-9) return "tie";
    if (higherIsBetter) return a > b ? "left" : "right";
    return a < b ? "left" : "right";
  };
  const cmpClasses = (side, w) => {
    if (!w || w === "tie") return "text-white";
    if (w === side) return "text-[#00FF88] font-bold";
    return "text-[#8A8A93]";
  };

  // Fmt helpers used inside the multi-leg panel.
  const fmtDollar = (v, dp = 2) => v != null && Number.isFinite(v) ? `$${Number(v).toFixed(dp)}` : "—";
  const fmtPct = (v, dp = 1) => v != null && Number.isFinite(v) ? `${Number(v).toFixed(dp)}%` : "—";
  const fmtRR = (v) => v != null && Number.isFinite(v) ? Number(v).toFixed(2) : "—";

  // Iter119.5 — Paste-ready broker order line for the pinned spread.
  // Two formats supported by the same builder:
  //   • Credit spread (BPCS / BCCS):
  //       SPY 2026-07-02 BPCS -763P / +755P @ 5.08 LMT × 1
  //   • Iron Condor (4 legs):
  //       SPY 2026-07-02 IC -714P/+710P / -745C/+750C @ 1.20 LMT × 1
  // Universal one-line format — most brokers (TOS, Tasty, Tradier,
  // IBKR) accept this pasted into chat or notes; the operator
  // adapts it to their broker's ticket entry. Read-only, no auto-send.
  const buildManualTicket = () => {
    if (!isMultiLeg || !pinnedSpread?.metrics) return null;
    const exp = data?.expiration || meta?.expiration || "";
    const credit = pinnedSpread.metrics.credit;
    if (credit == null || !Number.isFinite(credit)) return null;
    const fmtLeg = (sign, row) => {
      if (!row) return null;
      const t = (row.type || "").toUpperCase().charAt(0) || "?";
      return `${sign}${Number(row.strike).toFixed(0)}${t}`;
    };
    let legs = "";
    if (moduleId === "IRON_CONDOR") {
      const sp = fmtLeg("-", pinnedSpread.putShortRow);
      const lp = fmtLeg("+", pinnedSpread.putLongRow);
      const sc = fmtLeg("-", pinnedSpread.callShortRow);
      const lc = fmtLeg("+", pinnedSpread.callLongRow);
      legs = `${sp}/${lp} / ${sc}/${lc}`;
    } else if (moduleId === "BPCS") {
      // Look up the put short (pinned) and long (auto-paired or manual).
      const short = pinnedRow;
      const longR = pinnedSpread.longRow;
      legs = `${fmtLeg("-", short)} / ${fmtLeg("+", longR)}`;
    } else if (moduleId === "BCCS") {
      const short = pinnedRow;
      const longR = pinnedSpread.longRow;
      legs = `${fmtLeg("-", short)} / ${fmtLeg("+", longR)}`;
    }
    const tag = moduleId === "IRON_CONDOR" ? "IC" : moduleId;
    const ticker = (symbol || "").toUpperCase();
    const n = clampContracts(contracts);
    return `${ticker} ${exp} ${tag} ${legs} @ ${credit.toFixed(2)} LMT × ${n}`;
  };

  return (
    <div data-testid={`scanner-chain-drawer-${symbol}-${moduleId}`}
         className="rounded-md border border-[#26262B] bg-[#08080A]/60">
      <button
        type="button"
        data-testid={`scanner-chain-drawer-toggle-${symbol}-${moduleId}`}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#8A8A93] hover:text-white"
      >
        <span className="flex items-center gap-1.5">
          <Layers size={11} className="text-[#22D3FF]" />
          {open ? "Hide chain" : "View chain"}
          <span className="text-[#5F5F66] normal-case font-normal tracking-normal">
            · {optionType === "put" ? "ITM puts" : optionType === "call" ? "OTM calls" : "puts + calls"}
          </span>
        </span>
        <ChevronDown size={11} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {/* Band toggle */}
          <div className="flex items-center justify-between gap-2">
            <div
              data-testid={`scanner-chain-band-toggle-${symbol}-${moduleId}`}
              role="group"
              aria-label="Chain strike band"
              className="inline-flex rounded-sm border border-[#26262B] overflow-hidden"
            >
              {CHAIN_BAND_OPTIONS.map((opt, i) => {
                const active = band === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    data-testid={`scanner-chain-band-${symbol}-${moduleId}-${opt.id}`}
                    onClick={() => setBand(opt.id)}
                    title={opt.title}
                    aria-pressed={active}
                    className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] transition-colors ${
                      active ? "bg-[#00FF88]/15 text-[#00FF88]"
                             : "bg-transparent text-[#8A8A93] hover:text-white hover:bg-[#26262B]/30"
                    } ${i > 0 ? "border-l border-[#26262B]" : ""}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <div className="text-[9px] font-mono text-[#5F5F66] text-right">
              {loading ? (
                <span className="inline-flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> Loading…</span>
              ) : meta.sigma_dollars ? (
                <span title={`ATM IV ${meta.atm_iv ? (meta.atm_iv * 100).toFixed(1) + "%" : "—"} · expected move ${meta.expected_move_pct ? meta.expected_move_pct.toFixed(2) + "%" : "—"}`}>
                  σ ±${Number(meta.sigma_dollars).toFixed(2)} ·
                  {" "}<span data-testid={`scanner-chain-row-count-${symbol}-${moduleId}`}>{chain.length}</span>
                  {meta.trim_applied ? `/${meta.rows_before_trim}` : ""} strikes
                </span>
              ) : (
                <span><span data-testid={`scanner-chain-row-count-${symbol}-${moduleId}`}>{chain.length}</span> strikes</span>
              )}
            </div>
          </div>

          {err && (
            <div data-testid={`scanner-chain-drawer-error-${symbol}-${moduleId}`}
                 className="text-[10px] font-mono text-[#FF3366] bg-[#FF3366]/10 border border-[#FF3366]/30 rounded-sm px-2 py-1.5">
              {err}
            </div>
          )}

          {/* Iter119.18 — Skeleton rows while loading + no chain rendered yet.
              Mirrors the table shape so the drawer doesn't pop. */}
          {!err && loading && chain.length === 0 && (
            <div
              data-testid={`scanner-chain-skeleton-${symbol}-${moduleId}`}
              className="rounded-sm border border-[#1A1A20] divide-y divide-[#1A1A20]"
            >
              <SkeletonRow cols={8} height="h-2.5" />
              <SkeletonRow cols={8} height="h-2.5" />
              <SkeletonRow cols={8} height="h-2.5" />
              <SkeletonRow cols={8} height="h-2.5" />
              <SkeletonRow cols={8} height="h-2.5" />
            </div>
          )}

          {!err && chain.length > 0 && (
            <div className="max-h-64 overflow-y-auto rounded-sm border border-[#1A1A20]">
              <table className="w-full text-[10px] font-mono">
                <thead className="sticky top-0 bg-[#0F0F14]">
                  <tr className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#5F5F66] border-b border-[#26262B]">
                    <th className="text-left  px-2 py-1.5">Strike</th>
                    <th className="text-left  px-2 py-1.5">Type</th>
                    <th className="text-right px-2 py-1.5">Bid</th>
                    <th className="text-right px-2 py-1.5">Ask</th>
                    <th className="text-right px-2 py-1.5">Δ</th>
                    <th className="text-right px-2 py-1.5">IV</th>
                    <th className="text-right px-2 py-1.5">OI</th>
                    <th className="text-right px-2 py-1.5" title="Click any row to pin for comparison">Pin</th>
                  </tr>
                </thead>
                <tbody>
                  {chain.map(row => {
                    const isHit = highlighted.has(Number(row.strike));
                    const isPinnedShort = pinnedStrike != null && Math.abs(Number(row.strike) - Number(pinnedStrike)) < 0.001;
                    // Iter119.4 — Long-leg pin (manual mode only). Coloured
                    // cyan to distinguish from the short's gold pin so the
                    // two-click flow is obvious at a glance.
                    const isPinnedLong = longLegMode === "manual" && pinnedLongStrike != null
                                          && Math.abs(Number(row.strike) - Number(pinnedLongStrike)) < 0.001;
                    const rowClasses = isPinnedShort
                      ? "bg-[#FFB800]/12 ring-1 ring-inset ring-[#FFB800]/60"
                      : isPinnedLong
                        ? "bg-[#22D3FF]/10 ring-1 ring-inset ring-[#22D3FF]/60"
                        : isHit
                          ? "bg-[#00FF88]/8 ring-1 ring-inset ring-[#00FF88]/30"
                          : "hover:bg-[#26262B]/30";
                    return (
                      <tr
                        key={`${row.type}-${row.strike}`}
                        data-testid={`scanner-chain-row-${symbol}-${moduleId}-${row.type}-${row.strike}`}
                        onClick={() => handleRowClick(row.strike)}
                        className={`border-b border-[#1A1A20] cursor-pointer transition-colors ${rowClasses}`}
                        title={
                          isPinnedShort ? "Click to unpin short" :
                          isPinnedLong  ? "Click to unpin long" :
                          longLegMode === "manual" && isMultiLeg && pinnedStrike != null
                            ? "Click to pin LONG leg (step 2)"
                            : "Click to pin for side-by-side comparison"
                        }
                      >
                        <td className="px-2 py-1 font-bold text-white">${Number(row.strike).toFixed(2)}</td>
                        <td className="px-2 py-1 uppercase text-[#8A8A93]">{row.type}</td>
                        <td className="px-2 py-1 text-right text-[#E4E4E7]">{row.bid?.toFixed(2)}</td>
                        <td className="px-2 py-1 text-right text-[#8A8A93]">{row.ask?.toFixed(2)}</td>
                        <td className="px-2 py-1 text-right text-[#8A8A93]">{row.delta != null ? Math.abs(row.delta).toFixed(2) : "—"}</td>
                        <td className="px-2 py-1 text-right text-[#8A8A93]">{row.iv != null ? (row.iv * 100).toFixed(1) + "%" : "—"}</td>
                        <td className="px-2 py-1 text-right text-[#8A8A93]">{row.open_interest?.toLocaleString() || 0}</td>
                        <td className="px-2 py-1 text-right">
                          {isPinnedShort
                            ? <Pin size={10} className="inline text-[#FFB800] rotate-45" />
                            : isPinnedLong
                              ? <Pin size={10} className="inline text-[#22D3FF] rotate-[135deg]" />
                              : <Pin size={10} className="inline text-[#5F5F66]" />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Iter119.4 — Long-leg pairing mode (always visible for
              multi-leg modules; lets the operator design a spread
              on-demand even on CTX_MISS / NO_TRADE cards where the
              engine never picked a structure). */}
          {isMultiLeg && (
            <div className="flex items-center gap-2 flex-wrap"
                 data-testid={`scanner-chain-longleg-mode-${symbol}-${moduleId}`}>
              <span className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#5F5F66]">Long leg</span>
              <div role="group"
                   aria-label="Long leg pairing mode"
                   className="inline-flex rounded-sm border border-[#26262B] overflow-hidden">
                {[
                  { id: "engine", label: "Engine width", title: "Match the engine's wing width (requires an engine-picked structure)" },
                  { id: "wes",    label: "WES anchor",  title: "Anchor the long leg to the nearest VAL/POC/VAH on the long-side" },
                  { id: "manual", label: "Manual",      title: "Two-click flow — pin SHORT first, then click a second row to pin LONG. Unlocks asymmetric / non-WES wing widths." },
                ].map((opt, i) => {
                  const active = longLegMode === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      data-testid={`scanner-chain-longleg-${symbol}-${moduleId}-${opt.id}`}
                      onClick={() => setLongLegMode(opt.id)}
                      title={opt.title}
                      aria-pressed={active}
                      className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] transition-colors ${
                        active ? "bg-[#22D3FF]/15 text-[#22D3FF]"
                               : "bg-transparent text-[#8A8A93] hover:text-white hover:bg-[#26262B]/30"
                      } ${i > 0 ? "border-l border-[#26262B]" : ""}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {longLegMode === "wes" && pinnedSpread?.autoLong?.source === "WES" && (
                <span data-testid={`scanner-chain-wes-anchor-applied-${symbol}-${moduleId}`}
                      className="text-[8px] font-mono text-[#22D3FF] bg-[#22D3FF]/10 border border-[#22D3FF]/30 rounded-sm px-1.5 py-0.5">
                  Anchored to {pinnedSpread.autoLong.anchor} ${Number(pinnedSpread.autoLong.anchor_value).toFixed(2)}
                </span>
              )}
              {longLegMode === "wes" && pinnedSpread?.autoLong != null && pinnedSpread?.autoLong?.source !== "WES" && (
                <span data-testid={`scanner-chain-wes-anchor-fallback-${symbol}-${moduleId}`}
                      className="text-[8px] font-mono text-[#FFB800] bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-sm px-1.5 py-0.5"
                      title="No VAL/POC/VAH level on the long-side of the pinned short; falling back to engine width">
                  WES level not on long-side · using engine width
                </span>
              )}
              {longLegMode === "manual" && pinnedSpread?.autoLong?.source === "MANUAL" && (
                <span data-testid={`scanner-chain-manual-applied-${symbol}-${moduleId}`}
                      className="text-[8px] font-mono text-[#22D3FF] bg-[#22D3FF]/10 border border-[#22D3FF]/30 rounded-sm px-1.5 py-0.5">
                  Manual pair · width ${pinnedSpread.metrics?.width?.toFixed(2)}
                </span>
              )}
            </div>
          )}

          {/* Iter119.4 — Manual mode step-2 hint. Renders when the
              operator picks short but hasn't picked long yet. */}
          {longLegMode === "manual" && isMultiLeg && pinnedStrike != null && pinnedLongStrike == null && (
            <div data-testid={`scanner-chain-manual-step2-${symbol}-${moduleId}`}
                 className="text-[9px] font-mono text-[#22D3FF] bg-[#22D3FF]/8 border border-[#22D3FF]/30 rounded-sm px-2 py-1.5 flex items-center justify-between gap-2">
              <span>Step 2 — click another row to pin the LONG leg of your spread.</span>
              <button
                type="button"
                data-testid={`scanner-chain-manual-cancel-${symbol}-${moduleId}`}
                onClick={() => { setPinnedStrike(null); setPinnedLongStrike(null); }}
                className="flex items-center gap-1 text-[#22D3FF] hover:text-white"
                title="Cancel manual pair"
              >
                <PinOff size={10} /> Cancel
              </button>
            </div>
          )}

          {/* Iter119.2 — Single-leg "no primary leg from engine" notice.
              Only fires for CFM modules; the multi-leg panel (Iter119.4)
              renders gracefully without a picked spread, so the
              warning is suppressed there. */}
          {!isMultiLeg && pinnedStrike != null && !pickedRow && pinnedRow && (
            <div data-testid={`scanner-chain-compare-no-picked-${symbol}-${moduleId}`}
                 className="text-[9px] font-mono text-[#FFB800] bg-[#FFB800]/8 border border-[#FFB800]/30 rounded-sm px-2 py-1.5">
              No primary leg from the engine to compare against on this card. (Status: {pinnedM ? "pinned only" : "—"})
            </div>
          )}
          {pinnedStrike != null && pinnedRow == null && (
            <div data-testid={`scanner-chain-compare-out-of-band-${symbol}-${moduleId}`}
                 className="text-[9px] font-mono text-[#FFB800] bg-[#FFB800]/8 border border-[#FFB800]/30 rounded-sm px-2 py-1.5 flex items-center justify-between gap-2">
              <span>Pinned strike ${Number(pinnedStrike).toFixed(2)} is outside the current band — widen to FULL to compare.</span>
              <button
                type="button"
                data-testid={`scanner-chain-compare-clear-${symbol}-${moduleId}`}
                onClick={() => setPinnedStrike(null)}
                className="flex items-center gap-1 text-[#FFB800] hover:text-white"
                title="Clear pinned strike"
              >
                <PinOff size={10} /> Clear
              </button>
            </div>
          )}
          {compareOpen && !isMultiLeg && (
            <div data-testid={`scanner-chain-compare-${symbol}-${moduleId}`}
                 className="rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/5 px-3 py-2">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#FFB800] flex items-center gap-1.5">
                  <Pin size={11} className="rotate-45" />
                  Strike Comparison
                  <span className="text-[8px] font-normal text-[#A0A0A8] normal-case tracking-normal">
                    · single-leg snapshot · greener = better as a sold leg
                  </span>
                </div>
                <button
                  type="button"
                  data-testid={`scanner-chain-compare-clear-${symbol}-${moduleId}`}
                  onClick={() => setPinnedStrike(null)}
                  className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.15em] text-[#FFB800] hover:text-white border border-[#FFB800]/40 hover:border-white px-1.5 py-0.5 rounded-sm"
                  title="Clear pinned strike (Esc)"
                >
                  <PinOff size={10} /> Clear pin
                </button>
              </div>

              {/* Side-by-side table. PICKED column shows the engine's
                  primary short leg; PINNED column shows the operator's
                  hypothetical. Differences are coloured: green = the
                  better-for-the-seller side, grey = worse. */}
              <div className="grid grid-cols-[1.1fr_1fr_1fr] gap-x-3 gap-y-1 text-[10px] font-mono">
                <div></div>
                <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#00FF88]">
                  Picked ({primaryLeg?.role?.replace(/_/g, " ") || "engine"})
                </div>
                <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#FFB800]">
                  Pinned (you)
                </div>

                <div className="text-[#8A8A93]">Strike</div>
                <div className="text-white font-bold">${pickedM.strike.toFixed(2)}</div>
                <div className="text-white font-bold">${pinnedM.strike.toFixed(2)}</div>

                <div className="text-[#8A8A93]">Δ <span className="text-[#5F5F66] normal-case">(lower better for sold)</span></div>
                <div className={cmpClasses("left",  winner(pickedM.delta, pinnedM.delta, false))}>
                  {pickedM.delta != null ? pickedM.delta.toFixed(2) : "—"}
                </div>
                <div className={cmpClasses("right", winner(pickedM.delta, pinnedM.delta, false))}>
                  {pinnedM.delta != null ? pinnedM.delta.toFixed(2) : "—"}
                </div>

                <div className="text-[#8A8A93]">Premium (bid)</div>
                <div className={cmpClasses("left",  winner(pickedM.bid, pinnedM.bid, true))}>
                  ${pickedM.bid.toFixed(2)}
                </div>
                <div className={cmpClasses("right", winner(pickedM.bid, pinnedM.bid, true))}>
                  ${pinnedM.bid.toFixed(2)}
                </div>

                <div className="text-[#8A8A93]">ROI <span className="text-[#5F5F66] normal-case">(bid/strike)</span></div>
                <div className={cmpClasses("left",  winner(pickedM.roiPct, pinnedM.roiPct, true))}>
                  {pickedM.roiPct != null ? pickedM.roiPct.toFixed(2) + "%" : "—"}
                </div>
                <div className={cmpClasses("right", winner(pickedM.roiPct, pinnedM.roiPct, true))}>
                  {pinnedM.roiPct != null ? pinnedM.roiPct.toFixed(2) + "%" : "—"}
                </div>

                <div className="text-[#8A8A93]">IV</div>
                <div className="text-white">{pickedM.iv != null ? (pickedM.iv * 100).toFixed(1) + "%" : "—"}</div>
                <div className="text-white">{pinnedM.iv != null ? (pinnedM.iv * 100).toFixed(1) + "%" : "—"}</div>

                <div className="text-[#8A8A93]">OI <span className="text-[#5F5F66] normal-case">(higher = better)</span></div>
                <div className={cmpClasses("left",  winner(pickedM.oi, pinnedM.oi, true))}>
                  {pickedM.oi?.toLocaleString() || 0}
                </div>
                <div className={cmpClasses("right", winner(pickedM.oi, pinnedM.oi, true))}>
                  {pinnedM.oi?.toLocaleString() || 0}
                </div>

                <div className="text-[#8A8A93]">Breakeven</div>
                <div className="text-white">${pickedM.breakeven.toFixed(2)}</div>
                <div className="text-white">${pinnedM.breakeven.toFixed(2)}</div>

                {pickedM.widthVsSpotPct != null && (
                  <>
                    <div className="text-[#8A8A93]">Strike vs spot</div>
                    <div className="text-white">{pickedM.widthVsSpotPct > 0 ? "+" : ""}{pickedM.widthVsSpotPct.toFixed(2)}%</div>
                    <div className="text-white">{pinnedM.widthVsSpotPct > 0 ? "+" : ""}{pinnedM.widthVsSpotPct.toFixed(2)}%</div>
                  </>
                )}
              </div>

              <div className="mt-2 text-[8px] font-mono text-[#5F5F66] italic">
                Read-only · single-leg comparison only. Multi-leg spread P&L (max risk,
                BE incl. long leg) requires the matching long leg — out of scope here.
              </div>
            </div>
          )}

          {/* Iter119.3 — Multi-leg spread comparison panel. Renders
              for BPCS / BCCS / IRON_CONDOR when the operator pins a
              short strike. Auto-pairs the long leg using either the
              engine's wing width (default) or the nearest VAL/POC/VAH
              level on the long-side (WES anchor). */}
          {compareOpen && isMultiLeg && (
            <div data-testid={`scanner-chain-compare-multi-${symbol}-${moduleId}`}
                 className="rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/5 px-3 py-2">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#FFB800] flex items-center gap-1.5">
                  <Pin size={11} className="rotate-45" />
                  Spread Comparison · {moduleId.replace(/_/g, " ")}
                </div>
                <div className="flex items-center gap-1.5">
                  {/* Iter119.6 — Contracts stepper. Sits next to the
                      "Copy ticket" button; only renders when a usable
                      spread is pinned (same gate as the copy button).
                      Operators sweep 1..99 contracts via the +/-
                      buttons or hand-type the count. Changing the
                      count clears the "Copied" affordance so the
                      operator knows to re-copy. */}
                  {pinnedSpread?.metrics?.credit != null && (
                    <div
                      data-testid={`scanner-chain-contracts-stepper-${symbol}-${moduleId}`}
                      className="inline-flex items-center border border-[#26262B] rounded-sm overflow-hidden"
                      title="Contract count for the copied ticket line"
                    >
                      <button
                        type="button"
                        data-testid={`scanner-chain-contracts-dec-${symbol}-${moduleId}`}
                        onClick={() => {
                          setContracts(c => clampContracts(c - 1));
                          setTicketCopied(false);
                        }}
                        disabled={clampContracts(contracts) <= 1}
                        aria-label="Decrement contracts"
                        className="px-1.5 py-0.5 text-[10px] font-bold text-[#8A8A93] hover:text-white hover:bg-[#26262B]/40 disabled:opacity-40 disabled:cursor-not-allowed border-r border-[#26262B]"
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min={1}
                        max={99}
                        step={1}
                        inputMode="numeric"
                        data-testid={`scanner-chain-contracts-input-${symbol}-${moduleId}`}
                        value={contracts}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === "") { setContracts(""); setTicketCopied(false); return; }
                          setContracts(clampContracts(raw));
                          setTicketCopied(false);
                        }}
                        onBlur={(e) => setContracts(clampContracts(e.target.value || 1))}
                        aria-label="Contract count"
                        className="w-10 px-1 py-0.5 text-[10px] font-mono font-bold text-center bg-transparent text-white focus:outline-none focus:bg-[#22D3FF]/10 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      />
                      <button
                        type="button"
                        data-testid={`scanner-chain-contracts-inc-${symbol}-${moduleId}`}
                        onClick={() => {
                          setContracts(c => clampContracts(c + 1));
                          setTicketCopied(false);
                        }}
                        disabled={clampContracts(contracts) >= 99}
                        aria-label="Increment contracts"
                        className="px-1.5 py-0.5 text-[10px] font-bold text-[#8A8A93] hover:text-white hover:bg-[#26262B]/40 disabled:opacity-40 disabled:cursor-not-allowed border-l border-[#26262B]"
                      >
                        +
                      </button>
                    </div>
                  )}
                  {/* Iter119.5 — Build manual ticket. Renders only when
                      the operator has actually pinned a usable spread.
                      Copies a paste-ready broker order line for the
                      PINNED legs (not the engine's picks — those have
                      the card-footer "Copy trade ticket" button). */}
                  {pinnedSpread?.metrics?.credit != null && (
                    <button
                      type="button"
                      data-testid={`scanner-chain-build-ticket-${symbol}-${moduleId}`}
                      onClick={async () => {
                        const line = buildManualTicket();
                        if (!line) return;
                        try {
                          await navigator.clipboard.writeText(line);
                        } catch {
                          const ta = document.createElement("textarea");
                          ta.value = line;
                          ta.style.position = "fixed";
                          ta.style.opacity = "0";
                          document.body.appendChild(ta);
                          ta.select();
                          try { document.execCommand("copy"); } catch { /* swallow */ }
                          document.body.removeChild(ta);
                        }
                        setTicketCopied(true);
                        window.setTimeout(() => setTicketCopied(false), 1800);
                      }}
                      title={`Copy a paste-ready broker order line for your pinned ${moduleId.replace(/_/g, " ")} spread`}
                      className={`flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm border transition-colors ${
                        ticketCopied
                          ? "border-[#00FF88]/60 text-[#00FF88] bg-[#00FF88]/10"
                          : "border-[#22D3FF]/40 text-[#22D3FF] hover:bg-[#22D3FF]/10 hover:border-[#22D3FF]"
                      }`}
                    >
                      {ticketCopied ? <Check size={10} /> : <Clipboard size={10} />}
                      <span data-testid={`scanner-chain-build-ticket-label-${symbol}-${moduleId}`}>
                        {ticketCopied ? "Copied" : "Copy ticket"}
                      </span>
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid={`scanner-chain-compare-clear-${symbol}-${moduleId}`}
                    onClick={() => setPinnedStrike(null)}
                    className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.15em] text-[#FFB800] hover:text-white border border-[#FFB800]/40 hover:border-white px-1.5 py-0.5 rounded-sm"
                    title="Clear pinned strike (Esc)"
                  >
                    <PinOff size={10} /> Clear pin
                  </button>
                </div>
              </div>

              {/* Leg breakdown — header row + 2 per-leg lines, replicated
                  for each wing in Iron Condor. Picked column shows the
                  engine's strikes; Pinned column shows the operator's
                  short + the auto-paired long. */}
              {moduleId === "IRON_CONDOR" ? (
                <>
                  <SpreadLegBlock
                    label="Put Wing"
                    pickedShort={engineLegs.sp ? rowOf(engineLegs.sp.strike, "put") : null}
                    pickedLong={engineLegs.lp ? rowOf(engineLegs.lp.strike, "put") : null}
                    pinnedShort={pinnedSpread?.putShortRow}
                    pinnedLong={pinnedSpread?.putLongRow}
                    pinnedSideMatch={pinnedSpread?.pinnedSide === "put"}
                    fmtDollar={fmtDollar}
                  />
                  <SpreadLegBlock
                    label="Call Wing"
                    pickedShort={engineLegs.sc ? rowOf(engineLegs.sc.strike, "call") : null}
                    pickedLong={engineLegs.lc ? rowOf(engineLegs.lc.strike, "call") : null}
                    pinnedShort={pinnedSpread?.callShortRow}
                    pinnedLong={pinnedSpread?.callLongRow}
                    pinnedSideMatch={pinnedSpread?.pinnedSide === "call"}
                    fmtDollar={fmtDollar}
                  />
                </>
              ) : (
                <SpreadLegBlock
                  label={moduleId === "BPCS" ? "Put Spread" : "Call Spread"}
                  pickedShort={moduleId === "BPCS" ? (engineLegs.sp ? rowOf(engineLegs.sp.strike, "put") : null)
                                                   : (engineLegs.sc ? rowOf(engineLegs.sc.strike, "call") : null)}
                  pickedLong={moduleId === "BPCS" ? (engineLegs.lp ? rowOf(engineLegs.lp.strike, "put") : null)
                                                  : (engineLegs.lc ? rowOf(engineLegs.lc.strike, "call") : null)}
                  pinnedShort={pinnedRow}
                  pinnedLong={pinnedSpread?.longRow}
                  pinnedSideMatch
                  fmtDollar={fmtDollar}
                />
              )}

              {/* Combined spread P&L summary — the key 5 metrics.
                  Iter119.4 — Picked column gracefully renders "—" when
                  the engine has no `selected_structure` (NO_TRADE /
                  CTX_MISS), so the operator can design a spread
                  on-demand and still see Net credit / Max-P / Max-L /
                  R:R / Credit-Width / BE for their pin. */}
              <div className="mt-3 grid grid-cols-[1.2fr_1fr_1fr] gap-x-3 gap-y-1 text-[10px] font-mono border-t border-[#FFB800]/20 pt-2">
                <div></div>
                <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#00FF88]">Picked (engine)</div>
                <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#FFB800]">Pinned (you)</div>

                <div className="text-[#8A8A93]">Net credit</div>
                <div className={cmpClasses("left",  winner(pickedSpread?.credit, pinnedSpread?.metrics?.credit, true))}>
                  {fmtDollar(pickedSpread?.credit)}
                </div>
                <div className={cmpClasses("right", winner(pickedSpread?.credit, pinnedSpread?.metrics?.credit, true))}>
                  {fmtDollar(pinnedSpread?.metrics?.credit)}
                </div>

                <div className="text-[#8A8A93]">Width</div>
                <div className="text-white">{fmtDollar(pickedSpread?.width)}</div>
                <div className="text-white">{fmtDollar(pinnedSpread?.metrics?.width)}</div>

                <div className="text-[#8A8A93]">Max profit <span className="text-[#5F5F66] normal-case">(per ct)</span></div>
                <div className={cmpClasses("left",  winner(pickedSpread?.max_profit, pinnedSpread?.metrics?.max_profit, true))}>
                  {fmtDollar(pickedSpread?.max_profit, 0)}
                </div>
                <div className={cmpClasses("right", winner(pickedSpread?.max_profit, pinnedSpread?.metrics?.max_profit, true))}>
                  {fmtDollar(pinnedSpread?.metrics?.max_profit, 0)}
                </div>

                <div className="text-[#8A8A93]">Max loss <span className="text-[#5F5F66] normal-case">(per ct, lower better)</span></div>
                <div className={cmpClasses("left",  winner(pickedSpread?.max_loss, pinnedSpread?.metrics?.max_loss, false))}>
                  {fmtDollar(pickedSpread?.max_loss, 0)}
                </div>
                <div className={cmpClasses("right", winner(pickedSpread?.max_loss, pinnedSpread?.metrics?.max_loss, false))}>
                  {fmtDollar(pinnedSpread?.metrics?.max_loss, 0)}
                </div>

                <div className="text-[#8A8A93]">R:R <span className="text-[#5F5F66] normal-case">(profit / loss)</span></div>
                <div className={cmpClasses("left",  winner(pickedSpread?.rr, pinnedSpread?.metrics?.rr, true))}>
                  {fmtRR(pickedSpread?.rr)}
                </div>
                <div className={cmpClasses("right", winner(pickedSpread?.rr, pinnedSpread?.metrics?.rr, true))}>
                  {fmtRR(pinnedSpread?.metrics?.rr)}
                </div>

                <div className="text-[#8A8A93]">Credit / Width <span className="text-[#5F5F66] normal-case">(Tasty ≥33%)</span></div>
                <div className={cmpClasses("left",  winner(pickedSpread?.credit_pct_width, pinnedSpread?.metrics?.credit_pct_width, true))}>
                  {fmtPct(pickedSpread?.credit_pct_width)}
                </div>
                <div className={cmpClasses("right", winner(pickedSpread?.credit_pct_width, pinnedSpread?.metrics?.credit_pct_width, true))}>
                  {fmtPct(pinnedSpread?.metrics?.credit_pct_width)}
                </div>

                {moduleId === "IRON_CONDOR" ? (
                  <>
                    <div className="text-[#8A8A93]">Lower BE</div>
                    <div className="text-white">{fmtDollar(pickedSpread?.lower_be)}</div>
                    <div className="text-white">{fmtDollar(pinnedSpread?.metrics?.lower_be)}</div>
                    <div className="text-[#8A8A93]">Upper BE</div>
                    <div className="text-white">{fmtDollar(pickedSpread?.upper_be)}</div>
                    <div className="text-white">{fmtDollar(pinnedSpread?.metrics?.upper_be)}</div>
                  </>
                ) : (
                  <>
                    <div className="text-[#8A8A93]">Breakeven</div>
                    <div className="text-white">{fmtDollar(pickedSpread?.breakeven)}</div>
                    <div className="text-white">{fmtDollar(pinnedSpread?.metrics?.breakeven)}</div>
                  </>
                )}
              </div>

              <div className="mt-2 text-[8px] font-mono text-[#5F5F66] italic">
                Read-only · 100-multiplier · max-loss uses worst-fill (short bid − long ask) · WES anchor uses daily VAL/POC/VAH (falls back to hourly if daily missing).
              </div>
            </div>
          )}

          {!err && !loading && chain.length === 0 && data && (
            <div data-testid={`scanner-chain-drawer-empty-${symbol}-${moduleId}`}
                 className="text-[9px] font-mono text-[#5F5F66] italic px-2 py-1.5">
              No strikes in the current band. Try widening to ±3σ or FULL.
            </div>
          )}

          <div className="text-[8px] font-mono text-[#5F5F66] italic">
            Read-only · highlighted rows = legs picked by the scanner for this strategy.
            Click any row to pin it for side-by-side comparison.
          </div>
        </div>
      )}
    </div>
  );
}

// ── The TradingCard — one per (symbol, module) for ENTER/WATCH ─────
function TradingCard({ symbol, moduleId, row, raw, spot, dte, ivr, regime, wes, isTopPick, onCopy, copied }) {
  const status = row.status || "NO_TRADE";
  const theme = STATUS_THEME[status] || STATUS_THEME.NO_TRADE;
  const s = raw?.selected_structure || raw || {};
  const conf = row.confidence_score ?? row.conf ?? 0;
  const risk = row.risk_score ?? row.risk ?? 0;
  const reasonCodes = row.reason_codes || [];
  const moduleLabel = MODULE_LABELS[moduleId] || moduleId;
  const moduleVerb = MODULE_VERB[moduleId] || moduleId;

  // Iter115.8 — module-aware fallback mapping.  CFM modules use
  // different field names than credit spreads, so the generic
  // `max_profit / max_loss / roi` chips render as `—` unless we
  // map the CFM-specific fields back to the canonical names.
  //   - CFM Shares: max_profit ≈ call_credit per share × 100
  //     (premium retained if not called); roi ≈
  //     extrinsic_roi_on_basis × 100.
  //   - CFM LEAPS: max_profit ≈ short_cc_credit per share × 100;
  //     max_loss ≈ (long_debit - short_credit) × 100; roi as
  //     above.
  const sNormalized = useMemo(() => {
    if (!s) return null;
    const out = { ...s };
    const num = (x) => (x == null || Number.isNaN(Number(x)) ? null : Number(x));
    const m = String(moduleId || "").toLowerCase();
    if (m === "cfm_shares" || m === "cfm_cc_shares") {
      if (out.max_profit == null && num(out.call_credit) != null) {
        out.max_profit = num(out.call_credit) * 100;
      }
      if (out.roi == null && num(out.extrinsic_roi_on_basis) != null) {
        out.roi = num(out.extrinsic_roi_on_basis) * 100;
      }
      if (out.breakeven == null && num(out.shares_breakeven) != null) {
        out.breakeven = num(out.shares_breakeven);
      }
    } else if (m === "cfm_leaps" || m === "cfm_cc_leaps") {
      const sc = num(out.short_cc_credit);
      const ld = num(out.long_debit ?? out.leap_debit);
      if (out.max_profit == null && sc != null) out.max_profit = sc * 100;
      if (out.max_loss == null && ld != null && sc != null) out.max_loss = (ld - sc) * 100;
      if (out.roi == null && num(out.extrinsic_roi_on_basis) != null) {
        out.roi = num(out.extrinsic_roi_on_basis) * 100;
      }
    }
    return out;
  }, [s, moduleId]);

  // Use the normalized struct downstream so all five modules
  // render comparable Max P / Max L / ROI chips.
  const sView = sNormalized || s;

  // Iter115.8 — derive probability-of-profit from the structure's
  // short-leg delta when the backend doesn't return an explicit
  // `prob_profit` field.  For a single short option, P(OTM at
  // expiry) ≈ 1 - |delta|.  For a CSP / short put: short_delta is
  // negative — we use |delta|.  Iron Condor sums both wings.
  const probOfProfit = useMemo(() => {
    if (!s) return null;
    if (s.prob_profit != null) return Number(s.prob_profit);
    if (s.pop != null) return Number(s.pop);
    const absD = (x) => (x == null ? null : Math.abs(Number(x)));
    const m = String(moduleId || "").toLowerCase();
    if (m === "iron_condor") {
      const dp = absD(s.short_put?.delta ?? s.short_put_delta);
      const dc = absD(s.short_call?.delta ?? s.short_call_delta);
      if (dp != null && dc != null) {
        const pop = 1 - dp - dc;
        return pop >= 0 && pop <= 1 ? pop : null;
      }
      return null;
    }
    const candidates = [
      s.short_delta, s.net_delta,
      s.short_call?.delta, s.short_put?.delta,
      s.delta,
    ];
    for (const c of candidates) {
      if (c == null) continue;
      const a = Math.abs(Number(c));
      if (!Number.isNaN(a) && a >= 0 && a <= 1) return 1 - a;
    }
    return null;
  }, [s, moduleId]);

  // Build the action-verb headline.
  const legs = Array.isArray(s.legs) ? s.legs : [];
  let actionLine = "";
  if (legs.length > 0) {
    actionLine = legs.map(l => {
      const a = String(l.action || "").toUpperCase();
      const t = String(l.type || "").toUpperCase();
      const k = l.strike != null ? `$${Number(l.strike).toFixed(2)}` : "?";
      return `${a === "SELL" ? "Sell" : "Buy"} ${t} @ ${k}`;
    }).join(" · ");
  } else if (s.short_call) {
    const k = s.short_call.strike;
    actionLine = `Hold 100 sh · Sell CALL @ $${k != null ? Number(k).toFixed(2) : "?"}`;
  } else {
    actionLine = moduleVerb;
  }

  const tradeText = useMemo(
    () => formatTradeTicket(symbol, moduleId, raw),
    [symbol, moduleId, raw],
  );

  // Iter119.1 — Strikes the scanner picked for this strategy. Used
  // by <ChainDrawer /> to highlight the chosen legs inside the
  // chain table so the operator can spot them at a glance.
  const structureStrikes = useMemo(() => {
    if (!s) return [];
    const out = [];
    const add = (k) => {
      if (k != null && Number.isFinite(Number(k))) out.push(Number(k));
    };
    (Array.isArray(s.legs) ? s.legs : []).forEach(l => add(l?.strike));
    if (s.short_call) add(s.short_call.strike);
    if (s.long_call)  add(s.long_call.strike);
    if (s.short_put)  add(s.short_put.strike);
    if (s.long_put)   add(s.long_put.strike);
    return out;
  }, [s]);

  // Iter119.2 — Primary leg for the strike-comparison overlay.
  // The "primary" leg is the income-generating short leg for credit
  // structures (BPCS short put, BCCS short call, CFM short call) or
  // the first SHORT-tagged leg for multi-leg structures (Iron Condor).
  // Falls back to the first leg in `legs[]` if nothing else matches.
  const primaryLeg = useMemo(() => {
    if (!s) return null;
    if (s.short_put)  return { strike: s.short_put.strike,  type: "put",  role: "SHORT_PUT" };
    if (s.short_call) return { strike: s.short_call.strike, type: "call", role: "SHORT_CALL" };
    if (Array.isArray(s.legs) && s.legs.length > 0) {
      const short = s.legs.find(l => /short/i.test(l?.role || "") || /short/i.test(l?.side || ""));
      const leg = short || s.legs[0];
      const t = (leg?.type || leg?.option_type || "").toLowerCase();
      return { strike: leg?.strike, type: t === "call" ? "call" : "put", role: leg?.role || "PRIMARY" };
    }
    return null;
  }, [s]);

  return (
    <div
      data-testid={`trading-card-${symbol}-${moduleId}`}
      className={`relative flex flex-col rounded-xl overflow-hidden transition-all hover:-translate-y-1 group bg-gradient-to-br from-[#0F0F14] to-[#08080A]`}
      style={{
        border: `1px solid ${theme.bg === "#00FF88" ? "rgba(0,255,136,0.25)" : theme.bg === "#FFB800" ? "rgba(255,184,0,0.20)" : "#26262B"}`,
        boxShadow: status === "ENTER"
          ? "0 0 0 1px rgba(0,255,136,0.10), 0 12px 30px -12px rgba(0,255,136,0.18)"
          : status === "WATCH"
            ? "0 0 0 1px rgba(255,184,0,0.10), 0 12px 30px -12px rgba(255,184,0,0.18)"
            : "0 8px 20px -10px rgba(0,0,0,0.6)",
      }}
    >
      {/* Top-pick ribbon */}
      {isTopPick && (
        <div
          data-testid={`top-pick-${symbol}`}
          className="absolute top-0 right-0 z-10 flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black rounded-bl-md font-extrabold text-[9px] tracking-[0.18em] uppercase"
          style={{ boxShadow: "0 4px 12px rgba(255,215,0,0.35)" }}
        >
          <Star size={10} fill="currentColor" /> Top Pick
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-4 pt-4">
        <div className="flex items-center gap-2.5">
          <CompanyLogo symbol={symbol} size={36} testid={`trading-card-logo`} />
          <div className="flex flex-col gap-0.5">
            <span className="text-2xl font-extrabold tracking-tight text-white leading-none">{symbol}</span>
            <span className="text-[9px] font-mono uppercase tracking-[0.18em] text-[#5F5F66]">{moduleLabel}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <StatusBadge status={status} big />
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] font-mono uppercase tracking-[0.18em] text-[#5F5F66]">CONF</span>
            <span className="text-[13px] font-mono font-extrabold" style={{ color: conf >= 70 ? "#00FF88" : conf >= 50 ? "#FFB800" : "#8A8A93" }}>{Math.round(conf)}</span>
            <span className="text-[9px] font-mono text-[#5F5F66]">/100</span>
          </div>
        </div>
      </div>

      {/* Stats Bento */}
      <div className="px-4 pt-3 grid grid-cols-4 gap-2">
        <DataChip label="Spot" value={spot != null ? `$${Number(spot).toFixed(2)}` : "—"} accent="#FFFFFF" testid={`chip-spot-${symbol}`} />
        <DataChip label="DTE" value={dte != null ? `${dte}d` : "—"} accent="#22D3FF" testid={`chip-dte-${symbol}`} />
        <DataChip label="IVR" value={ivr != null ? `${Number(ivr).toFixed(0)}` : "—"} accent={ivr >= 50 ? "#00FF88" : "#FFB800"} testid={`chip-ivr-${symbol}`} />
        <DataChip label="Regime" value={regime || "—"} accent="#E5E5E7" testid={`chip-regime-${symbol}`} />
      </div>

      {/* Hero P/L payoff chart */}
      <div className="mx-4 mt-3 rounded-md border border-[#22D3FF]/15 bg-[#08080A] overflow-hidden flex items-center justify-center" data-testid={`payoff-hero-${symbol}-${moduleId}`}>
        {raw?.selected_structure ? (
          <PayoffChart
            structure={raw.selected_structure}
            spot={spot}
            width={360}
            height={150}
            testid={`payoff-hero`}
          />
        ) : (
          <div className="py-8 text-[10px] font-mono uppercase tracking-[0.16em] text-[#3F3F47]">
            No payoff sketch — context missing
          </div>
        )}
      </div>

      {/* WES strip */}
      <div className="px-4 pt-3">
        <WesStrip wes={wes} testid={`wes-${symbol}-${moduleId}`} />
      </div>

      {/* Trade metrics */}
      <div className="px-4 pt-3 grid grid-cols-5 gap-2">
        <DataChip label="Max P" value={sView.max_profit != null ? `$${Number(sView.max_profit).toFixed(0)}` : "—"} accent="#00FF88" testid={`chip-maxp-${symbol}`} />
        <DataChip label="Max L" value={sView.max_loss != null ? `$${Number(sView.max_loss).toFixed(0)}` : "—"} accent="#FF3366" testid={`chip-maxl-${symbol}`} />
        <DataChip label="ROI"   value={sView.roi != null ? `${Number(sView.roi).toFixed(0)}%` : (sView.ror != null ? `${Number(sView.ror).toFixed(0)}%` : "—")} accent="#FFD700" testid={`chip-roi-${symbol}`} />
        {/* Iter115.8 — probability of profit, derived from short
            leg delta when no explicit field is present. */}
        <DataChip
          label="POP"
          value={probOfProfit != null ? `${(probOfProfit * 100).toFixed(0)}%` : "—"}
          accent={probOfProfit != null && probOfProfit >= 0.70 ? "#00FF88" : probOfProfit != null && probOfProfit >= 0.55 ? "#FFB800" : "#8A8A93"}
          testid={`chip-pop-${symbol}`}
        />
        <DataChip label="BE"
          value={sView.breakeven != null
            ? (Array.isArray(sView.breakeven)
                ? sView.breakeven.map(b => `$${Number(b).toFixed(0)}`).join("/")
                : `$${Number(sView.breakeven).toFixed(2)}`)
            : (sView.shares_breakeven != null ? `$${Number(sView.shares_breakeven).toFixed(2)}` : "—")}
          accent="#22D3FF" testid={`chip-be-${symbol}`} />
      </div>

      {/* Action verb line + Reason chips */}
      <div className="px-4 pt-3 pb-3">
        <div className="rounded-md bg-[#121216] border border-[#26262B] px-3 py-2">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-[#5F5F66]">Action</div>
            {/* Iter115.8 — Why this verdict? popover.  Renders the
                doctrine row(s) that matched the engine's
                reason_codes for THIS card so the operator never
                has to guess. */}
            <WhyVerdictTrigger
              symbol={symbol}
              moduleId={moduleId}
              status={status}
              decision={row.decision}
              reasonCodes={reasonCodes}
            />
          </div>
          <div className="text-[12px] font-medium text-white leading-tight" data-testid={`action-line-${symbol}-${moduleId}`}>
            {actionLine}
          </div>
          {reasonCodes.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {reasonCodes.slice(0, 6).map((r, i) => (
                <span key={i} className="px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-[0.10em] bg-[#22D3FF]/10 text-[#22D3FF] border border-[#22D3FF]/20">{r}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Iter119.1 — Per-card lazy chain drawer. Read-only deep dive
          into the option chain around this strategy's strikes.
          Iter119.2 — pass the primaryLeg so the drawer can render a
          side-by-side comparison vs an operator-pinned strike.
          Iter119.3 — pass the full selected_structure + WES so the
          drawer can build full credit-spread metrics and offer
          WES-anchored long-leg auto-pairing. */}
      <div className="px-4 pb-3">
        <ChainDrawer
          symbol={symbol}
          moduleId={moduleId}
          structureStrikes={structureStrikes}
          primaryLeg={primaryLeg}
          selectedStructure={s}
          spot={spot}
          wes={wes}
        />
      </div>

      {/* Footer actions */}
      <div className="mt-auto px-4 pb-4 flex gap-2">
        <button
          type="button"
          data-testid={`copy-trade-ticket-${symbol}-${moduleId}`}
          onClick={() => onCopy(tradeText, `${symbol}-${moduleId}`)}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-[10px] font-bold uppercase tracking-[0.16em] transition-all ${copied ? "bg-[#00FF88] text-[#08080A]" : "bg-[#26262B] text-white hover:bg-[#00FF88] hover:text-[#08080A]"}`}
          title="Copy a plain-text trade ticket to your clipboard. Place the trade manually at your broker."
        >
          {copied ? <ClipboardCheck size={12} /> : <Clipboard size={12} />}
          {copied ? "Copied" : "Copy Trade Ticket"}
        </button>
        <button
          type="button"
          data-testid={`send-to-broker-${symbol}-${moduleId}`}
          disabled
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-[10px] font-bold uppercase tracking-[0.16em] border border-dashed border-[#26262B] text-[#3F3F47] cursor-not-allowed"
          title="Coming soon — direct broker order placement."
        >
          <Send size={12} /> Broker (Soon)
        </button>
      </div>

      {/* Risk strip footer */}
      <div className="px-4 pb-3 -mt-1">
        <div className="flex items-center justify-between text-[8px] font-mono uppercase tracking-[0.18em] text-[#5F5F66]">
          <span>Risk score: <span style={{ color: risk >= 70 ? "#FF3366" : risk >= 40 ? "#FFB800" : "#5F5F66" }}>{Math.round(risk)}/100</span></span>
          <span>{moduleId}</span>
        </div>
      </div>
    </div>
  );
}

// ── Watchlist Manager (inline dialog) ──────────────────────────────
function WatchlistManager({ open, onClose, items, addSymbol, removeSymbol, addingSymbol, isAdmin }) {
  const [draft, setDraft] = useState("");
  if (!open) return null;
  const personal = items.filter(it => it.scope === "personal");
  const shared = items.filter(it => it.scope === "shared");
  return (
    <div
      data-testid="watchlist-manager-overlay"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        data-testid="watchlist-manager-dialog"
        className="relative w-full max-w-lg rounded-xl bg-gradient-to-br from-[#0F0F14] to-[#08080A] border border-[#26262B] shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#26262B]">
          <div className="flex items-center gap-2">
            <Bookmark size={14} className="text-[#FFD700]" />
            <h3 className="text-[13px] font-bold tracking-wider uppercase text-white">Watchlist</h3>
          </div>
          <button
            type="button"
            data-testid="watchlist-manager-close"
            onClick={onClose}
            className="text-[#8A8A93] hover:text-white"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Add row */}
          <div className="flex items-center gap-2">
            <input
              data-testid="watchlist-manager-input"
              type="text"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && draft.trim()) {
                  addSymbol(draft.trim().toUpperCase());
                  setDraft("");
                }
              }}
              placeholder="Add ticker (e.g., AAPL)"
              spellCheck={false}
              autoComplete="off"
              className="flex-1 bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] outline-none rounded-md px-3 py-2 text-[12px] font-mono uppercase text-white placeholder:text-[#3F3F47]"
            />
            <button
              type="button"
              data-testid="watchlist-manager-add"
              onClick={() => { if (draft.trim()) { addSymbol(draft.trim().toUpperCase()); setDraft(""); } }}
              disabled={!draft.trim() || addingSymbol}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-[10px] font-bold uppercase tracking-[0.14em] bg-[#00FF88] text-[#08080A] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#33FF9F]"
            >
              {addingSymbol ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
              Add
            </button>
          </div>
          <div className="text-[10px] font-mono text-[#5F5F66] leading-relaxed">
            {isAdmin
              ? "You are an admin — symbols you add go into the SHARED pool visible to every operator."
              : "Symbols you add here are PERSONAL — only you see them.  Shared symbols below are curated by your admin."}
          </div>

          {/* Personal list */}
          {personal.length > 0 && (
            <div>
              <div className="text-[9px] font-mono uppercase tracking-[0.16em] text-[#22D3FF] mb-2">Personal · {personal.length}</div>
              <div className="flex flex-wrap gap-1.5" data-testid="watchlist-manager-personal-list">
                {personal.map(it => (
                  <span key={it.id || it.symbol} className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-[#22D3FF]/10 border border-[#22D3FF]/30 text-[#22D3FF] text-[10px] font-mono">
                    {it.symbol}
                    <button
                      type="button"
                      data-testid={`watchlist-manager-remove-${it.symbol}`}
                      onClick={() => removeSymbol(it.symbol)}
                      className="hover:text-white"
                    ><X size={9} /></button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Shared list */}
          {shared.length > 0 && (
            <div>
              <div className="text-[9px] font-mono uppercase tracking-[0.16em] text-[#FFD700] mb-2">Shared · {shared.length}</div>
              <div className="flex flex-wrap gap-1.5" data-testid="watchlist-manager-shared-list">
                {shared.map(it => (
                  <span key={it.id || it.symbol} className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-[#FFD700]/10 border border-[#FFD700]/30 text-[#FFD700] text-[10px] font-mono">
                    {it.symbol}
                    {isAdmin && (
                      <button
                        type="button"
                        data-testid={`watchlist-manager-remove-${it.symbol}`}
                        onClick={() => removeSymbol(it.symbol)}
                        className="hover:text-white"
                      ><X size={9} /></button>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}

          {items.length === 0 && (
            <div className="py-6 text-center text-[11px] font-mono text-[#5F5F66]" data-testid="watchlist-manager-empty">
              No symbols yet. Type a ticker above to start.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────
export default function StrategyScannerV2() {
  // Reads.
  const [watchlistItems, setWatchlistItems] = useState([]);
  // Iter121.3 — Scanner respects the user's active watchlist so
  // "scan target = WATCHLIST" runs against whatever list is currently
  // selected across the app. Same source of truth as MarketForge +
  // Dashboard + MarketScannerBridgeTile.
  const {
    watchlists,
    activeId: activeWatchlistId,
    switchActive: switchActiveWatchlist,
    refetch: refetchWatchlists,
  } = useActiveWatchlist();
  const [showManageWatchlists, setShowManageWatchlists] = useState(false);
  const [bridgeAware, setBridgeAware] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [rows, setRows] = useState([]);
  const [snapshotMeta, setSnapshotMeta] = useState(null);
  const [wesMeta, setWesMeta] = useState(null);
  // Iter115.6 — context-build telemetry.  Captures the `warnings`
  // array returned by `/forge/toolbox/scanner/unified-preview` so
  // the operator can see which submitted symbols failed to build
  // a Market Snapshot AND WES context (the two preconditions for
  // any per-module evaluation).  Read-only display; never feeds
  // back into the scan request.
  const [buildWarnings, setBuildWarnings] = useState([]);
  // Iter115.6 — ad-hoc single-symbol override.  When set, the
  // Strategy Scanner ignores the watchlist + bridge + URL set
  // and analyses ONLY this symbol.  Lets the operator probe
  // an off-universe ticker directly without going through the
  // Market Scanner first.  Cleared via the X button.
  const [adHocSymbol, setAdHocSymbol] = useState("");
  const [adHocInput, setAdHocInput] = useState("");
  // Iter115.7 — gating doctrine viewer.  When toggled, opens a
  // read-only modal that shows the operator the EXACT rules each
  // of the five modules uses to block / disapprove a strategy.
  // Mirrors the docstrings at the top of each evaluator
  // (`bpcs/evaluator.py`, `bccs/evaluator.py`, etc.) so the UI
  // and the engine can never drift out of sync.
  const [doctrineOpen, setDoctrineOpen] = useState(false);
  const [lastScanAt, setLastScanAt] = useState(null);
  const [statusFilter, setStatusFilter] = useState("actionable");
  const [topPicksOnly, setTopPicksOnly] = useState(false);
  const [watchlistOpen, setWatchlistOpen] = useState(false);
  const [addingSymbol, setAddingSymbol] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  // Iter112 — Live-scan state machine.  Default to LIVE when the
  // dev flag is on (operator chose "auto-start" in the 7D sign-off);
  // otherwise stays IDLE forever and the toggle is hidden.
  const [liveMode, setLiveMode] = useState(
    LIVE_SCAN_ENABLED ? LIVE_MODE_LIVE : LIVE_MODE_IDLE,
  );
  const [lastLiveTickAt, setLastLiveTickAt] = useState(null);
  const [liveTickCount, setLiveTickCount] = useState(0);
  const lastResultHashRef = useRef("");
  const liveIntervalRef = useRef(null);
  // Iter113.2 — Journal-context mirror chip state (most-recent
  // Decision Journal entry).  Null until the on-mount fetch lands;
  // null again when the operator has no entries.
  const [lastJournalEntry, setLastJournalEntry] = useState(null);
  const navigate = useNavigate();
  const { pass: bridgePass, clear: clearBridge } = useMarketScannerBridge();
  const runIdRef = useRef(0);

  const isAdmin = currentUser?.role === "admin";

  // 1) Initial load — current user + watchlist.
  const loadWatchlist = useCallback(async () => {
    try {
      // Iter121.3 — Scope items fetch to the currently-active list. When
      // no active id is known yet (first render), fall back to the
      // legacy combined view so the surface never renders empty.
      const params = activeWatchlistId ? { params: { watchlist_id: activeWatchlistId } } : undefined;
      const wl = await axios.get(`${API}/watchlist`, params);
      setWatchlistItems(Array.isArray(wl.data) ? wl.data : []);
    } catch (_e) { setWatchlistItems([]); }
  }, [activeWatchlistId]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const me = await axios.get(`${API}/auth/me`);
        if (mounted) setCurrentUser(me.data);
      } catch (_e) { /* ignore */ }
      await loadWatchlist();
      // Iter113.2 — single read-only fetch of the most-recent
      // Decision Journal entry.  No polling, no new endpoint.
      try {
        const j = await axios.get(`${API}/forge/journal/decisions?limit=1`);
        if (mounted) {
          const rows = j?.data?.rows;
          setLastJournalEntry(Array.isArray(rows) && rows.length > 0 ? rows[0] : null);
        }
      } catch (_e) { if (mounted) setLastJournalEntry(null); }
    })();
    return () => { mounted = false; };
  }, [loadWatchlist]);

  // Resolved symbol set: UNION of (a) URL `?symbols=` query (for
  // the Iter109 Market Scanner Push-button flow + ad-hoc deep
  // links), (b) saved per-user watchlist, and (c) the in-memory
  // Market Scanner bridge.  Uppercased, deduped, capped at 50 to
  // match the backend hard cap.
  const urlSymbols = useMemo(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const raw = sp.get("symbols");
      if (!raw) return [];
      return raw.split(/[,\s]+/)
        .map(s => s.trim().toUpperCase())
        .filter(Boolean);
    } catch (_e) { return []; }
  }, []);

  const resolvedSymbols = useMemo(() => {
    // Iter115.6 — ad-hoc single-symbol override path.  When the
    // operator has typed a one-off ticker in the header input,
    // the Strategy Scanner analyses ONLY that symbol — no
    // watchlist, no bridge, no URL set.  Lets us probe off-
    // universe names directly without going through the Market
    // Scanner first.
    if (adHocSymbol) {
      const s = String(adHocSymbol).trim().toUpperCase();
      return s ? [s] : [];
    }
    const set = new Set();
    urlSymbols.forEach(s => set.add(s));
    watchlistItems.forEach(it => {
      const s = String(it.symbol || "").trim().toUpperCase();
      if (s) set.add(s);
    });
    if (bridgeAware && bridgePass?.symbols) {
      bridgePass.symbols.forEach(s => {
        const u = String(s || "").trim().toUpperCase();
        if (u) set.add(u);
      });
    }
    return Array.from(set).slice(0, 50);
  }, [adHocSymbol, urlSymbols, watchlistItems, bridgePass, bridgeAware]);

  // 2) Scan handler — POST to unified-preview with context_mode=live.
  //    `isLiveTick` distinguishes operator-triggered scans (initial
  //    mount, refresh button, symbol-set change) from background
  //    LIVE cadence ticks.  Live ticks use the anti-flash merge +
  //    result-hash short-circuit so the UI never reflows when the
  //    backend payload hasn't actually changed.
  const runScan = useCallback(async (symbols, isLiveTick = false) => {
    if (!symbols || symbols.length === 0) {
      setRows([]); setScanError(null); return;
    }
    runIdRef.current += 1;
    const myId = runIdRef.current;
    if (!isLiveTick) setScanning(true);
    setScanError(null);
    try {
      const resp = await axios.post(
        `${API}/forge/toolbox/scanner/unified-preview`,
        {
          symbols,
          modules_enabled: {
            bpcs: true, bccs: true, iron_condor: true,
            cfm_shares: true, cfm_leaps: true,
          },
          max_symbols: 50,
          context_mode: "live",
        },
      );
      if (runIdRef.current !== myId) return;
      const data = resp.data || {};
      const nextRows = Array.isArray(data.rows) ? data.rows : [];
      // Result-hash short-circuit — skip the React setState
      // entirely when the payload is byte-equivalent to the
      // previous tick.  No reconcile, no reflow, no DOM churn.
      const nextHash = liveScanResultHash(nextRows);
      if (nextHash === lastResultHashRef.current && isLiveTick) {
        // Touch the tick-stamp so the heartbeat dot still pulses
        // (the data IS fresh — just unchanged).
        setLastLiveTickAt(new Date().toISOString());
        return;
      }
      lastResultHashRef.current = nextHash;
      // Anti-flash merge for live ticks; fresh array replace for
      // operator-triggered scans (so symbol-set changes apply
      // cleanly).
      if (isLiveTick) {
        setRows(prev => mergeRowsByStableKey(prev, nextRows));
      } else {
        setRows(nextRows);
      }
      setSnapshotMeta(data.internal_snapshot_meta || null);
      setWesMeta(data.internal_wes_meta || null);
      // Iter115.6 — capture per-symbol context-build warnings so
      // the operator can see exactly which names had no snapshot
      // + no WES (the only reason a row falls into CTX MISS for
      // every module).
      setBuildWarnings(Array.isArray(data.warnings) ? data.warnings : []);
      setLastScanAt(new Date().toISOString());
      if (isLiveTick) setLastLiveTickAt(new Date().toISOString());
    } catch (err) {
      if (runIdRef.current !== myId) return;
      const detail = err?.response?.data?.detail || err?.message || "Scan failed";
      setScanError(typeof detail === "string" ? detail : JSON.stringify(detail));
      // Iter112 — auto-PAUSED on ANY error response (per Phase 7D
      // sign-off): rate-limit, network, 500, etc.  Operator must
      // explicitly click Resume to re-enter LIVE.
      if (LIVE_SCAN_ENABLED && isLiveTick) {
        setLiveMode(LIVE_MODE_PAUSED);
      }
      if (!isLiveTick) setRows([]);
    } finally {
      if (runIdRef.current === myId && !isLiveTick) setScanning(false);
    }
  }, []);

  // 3) Auto-scan on resolved-symbols change.  This is the "automatic"
  //    flow the operator asked for — no Run button.
  const lastScannedKeyRef = useRef("");
  useEffect(() => {
    if (resolvedSymbols.length === 0) {
      setRows([]); return;
    }
    const key = resolvedSymbols.join(",");
    if (key === lastScannedKeyRef.current) return;
    lastScannedKeyRef.current = key;
    runScan(resolvedSymbols);
  }, [resolvedSymbols, runScan]);

  // 3b) Iter112 — LIVE-mode cadence interval.  Hard-gated on three
  //     conditions: (a) the dev flag is on, (b) the operator is in
  //     LIVE mode (not IDLE / not PAUSED), (c) at least one symbol
  //     resolved.  PAUSED state never schedules a tick.
  useEffect(() => {
    // Clear any existing interval before deciding what to do next.
    if (liveIntervalRef.current) {
      window.clearInterval(liveIntervalRef.current);
      liveIntervalRef.current = null;
    }
    if (!LIVE_SCAN_ENABLED) return;
    if (liveMode !== LIVE_MODE_LIVE) return;       // PAUSED hard-gate
    if (resolvedSymbols.length === 0) return;
    const tick = () => { runScan(resolvedSymbols, true); };
    liveIntervalRef.current = window.setInterval(tick, LIVE_CADENCE_MS);
    return () => {
      if (liveIntervalRef.current) {
        window.clearInterval(liveIntervalRef.current);
        liveIntervalRef.current = null;
      }
    };
  }, [liveMode, resolvedSymbols, runScan]);

  // 3c) Iter112 — Bump tick counter when a live tick lands so the
  //     "live ticks · N" badge stays accurate.
  useEffect(() => {
    if (!lastLiveTickAt) return;
    setLiveTickCount(c => c + 1);
  }, [lastLiveTickAt]);

  // 4) Watchlist add/remove handlers.
  const handleAdd = async (sym) => {
    setAddingSymbol(true);
    try {
      const params = activeWatchlistId ? { watchlist_id: activeWatchlistId } : {};
      await axios.post(`${API}/watchlist`, { symbol: sym }, { params });
      await Promise.all([loadWatchlist(), refetchWatchlists(activeWatchlistId)]);
    } catch (_e) { /* swallow — server may 409 on dup */ }
    finally { setAddingSymbol(false); }
  };
  const handleRemove = async (sym) => {
    try {
      const params = activeWatchlistId ? { watchlist_id: activeWatchlistId } : {};
      await axios.delete(`${API}/watchlist/${encodeURIComponent(sym)}`, { params });
      await Promise.all([loadWatchlist(), refetchWatchlists(activeWatchlistId)]);
    } catch (_e) { /* ignore */ }
  };

  // 5) Copy-trade-ticket handler.
  const handleCopy = async (txt, key) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(txt);
      }
    } catch (_e) { /* ignore — non-https may block */ }
    setCopiedKey(key);
    window.setTimeout(() => setCopiedKey(prev => (prev === key ? null : prev)), 1800);
  };

  // 6) Per-symbol metadata extraction.  Top-pick selection by max
  //    confidence among actionable rows.
  //    Iter117 — prefer `r.context_metrics.*` (injected by the backend
  //    unified-preview aggregator) over the legacy raw-payload sniff so
  //    Spot / DTE / IVR / Regime / WES finally render on every card.
  const symbolMeta = useMemo(() => {
    const out = {};
    rows.forEach(r => {
      const sym = r.symbol;
      if (!out[sym]) out[sym] = { topModule: null, topConf: -1 };
      const cm = r.context_metrics || {};
      const raw = r.raw_module_response || {};
      // Authoritative top-level metrics from the aggregator's context
      // bundle. Fall back to the older raw-response sniff only when the
      // backend didn't attach the new field (older preview runs).
      if (out[sym].spot == null) {
        out[sym].spot = cm.spot ?? raw.spot_price ?? raw.spot ?? null;
      }
      if (out[sym].dte == null) {
        out[sym].dte = cm.dte ?? raw.dte ?? null;
      }
      if (out[sym].ivr == null) {
        out[sym].ivr = cm.ivr ?? raw.ivr ?? null;
      }
      if (out[sym].regime == null) {
        out[sym].regime = cm.regime ?? raw.regime ?? raw.h_regime ?? raw.market_regime ?? null;
      }
      // WES strip — use the {wes_p, wes_c, regime} shape the chip expects.
      if (out[sym].wes == null) {
        if (cm.wes_p != null || cm.wes_c != null) {
          out[sym].wes = { wes_p: cm.wes_p, wes_c: cm.wes_c, regime: cm.regime };
        } else if (raw.wes) {
          out[sym].wes = raw.wes;
        }
      }
      // Top pick.
      if (ACTIONABLE_STATUSES.has(r.status)) {
        const conf = r.confidence_score ?? 0;
        if (conf > out[sym].topConf) {
          out[sym].topConf = conf;
          out[sym].topModule = r.module_id;
        }
      }
    });
    return out;
  }, [rows]);

  // 7) Hero KPI counts.
  const kpi = useMemo(() => {
    const c = { ENTER: 0, WATCH: 0, NO_TRADE: 0, BLOCKED: 0, CONTEXT_MISSING: 0, ERROR: 0 };
    rows.forEach(r => { c[r.status] = (c[r.status] || 0) + 1; });
    const enterSyms = new Set(rows.filter(r => r.status === "ENTER").map(r => r.symbol));
    return { ...c, enterSymbols: enterSyms.size, totalSymbols: resolvedSymbols.length };
  }, [rows, resolvedSymbols]);

  // 8) Filtering.  CTX_MISS rows are noise on every filter EXCEPT
  //    the explicit "blocked / ctx miss" view — operator confirmed
  //    in Iter111 sign-off that they want CTX_MISS cards hidden by
  //    default and only revealed via the Blocked / CTX miss filter.
  const visibleRows = useMemo(() => {
    let out = rows;
    if (statusFilter === "actionable") {
      out = out.filter(r => ACTIONABLE_STATUSES.has(r.status));
    } else if (statusFilter === "enter") {
      out = out.filter(r => r.status === "ENTER");
    } else if (statusFilter === "watch") {
      out = out.filter(r => r.status === "WATCH");
    } else if (statusFilter === "blocked") {
      out = out.filter(r => r.status === "BLOCKED" || r.status === "CONTEXT_MISSING" || r.status === "ERROR");
    } else {
      // statusFilter === "all" — still hide CTX_MISS noise.
      out = out.filter(r => r.status !== "CONTEXT_MISSING");
    }
    if (topPicksOnly) {
      out = out.filter(r => symbolMeta[r.symbol]?.topModule === r.module_id);
    }
    return out;
  }, [rows, statusFilter, topPicksOnly, symbolMeta]);

  // ── Render ────────────────────────────────────────────────────
  return (
    <div data-testid="strategy-scanner-v2" className="p-6 lg:p-8 max-w-[1500px] mx-auto">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Radar size={16} className="text-[#22D3FF]" />
            <h1 data-testid="strategy-scanner-v2-title" className="text-[26px] font-extrabold tracking-tight text-white">Strategy Scanner</h1>
            <span className="text-[9px] font-mono uppercase tracking-[0.20em] text-[#22D3FF] border border-[#22D3FF]/40 rounded-sm px-1.5 py-0.5 ml-1">v2 · auto</span>
            {/* Iter115.7 — open the in-app gating doctrine reference. */}
            <button
              type="button"
              data-testid="open-gating-doctrine"
              onClick={() => setDoctrineOpen(true)}
              title="Open the gating doctrine — what each module checks before approving a strategy."
              className="ml-2 text-[10px] font-mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-sm border border-[#22D3FF]/40 text-[#22D3FF] bg-[#22D3FF]/5 hover:bg-[#22D3FF]/15 inline-flex items-center gap-1"
            >
              <span>?</span>
              <span>Gating Doctrine</span>
            </button>
          </div>
          <p className="text-[11px] font-mono text-[#5F5F66] tracking-tight">
            Watchlist ∪ Market Scanner bridge · live snapshot + WES · five-module evaluator · read-only, no auto-execute.
          </p>
          {/* Iter115.6 — ad-hoc single-symbol probe.  Lets the
              operator analyse ONE off-universe ticker directly
              without touching the watchlist or Market Scanner
              bridge.  Type a symbol, hit Enter, and the next
              scan pass replaces the resolved set with just that
              symbol.  Click X to return to the watchlist/bridge
              union. */}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <label className="text-[9px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">Ad-hoc symbol</label>
            <input
              type="text"
              data-testid="adhoc-symbol-input"
              value={adHocInput}
              onChange={e => setAdHocInput(e.target.value.toUpperCase())}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  const s = adHocInput.trim().toUpperCase();
                  if (s) setAdHocSymbol(s);
                }
              }}
              placeholder="e.g. BABA — analyses ONE ticker, off-universe OK"
              className="bg-[#08080A] border border-[#22D3FF]/30 focus:border-[#22D3FF] text-white rounded-sm px-2 py-1 text-xs font-mono w-[280px]"
            />
            <button
              type="button"
              data-testid="adhoc-symbol-analyze"
              onClick={() => {
                const s = adHocInput.trim().toUpperCase();
                if (s) setAdHocSymbol(s);
              }}
              disabled={!adHocInput.trim()}
              className="text-[10px] uppercase tracking-[0.12em] px-3 py-1 rounded-sm border border-[#22D3FF]/40 text-[#22D3FF] bg-[#22D3FF]/10 hover:bg-[#22D3FF]/20 disabled:opacity-30 font-bold"
            >Analyse</button>
            {adHocSymbol && (
              <button
                type="button"
                data-testid="adhoc-symbol-clear"
                onClick={() => { setAdHocSymbol(""); setAdHocInput(""); }}
                title={`Currently analysing only ${adHocSymbol}. Click to return to the watchlist + bridge union.`}
                className="text-[10px] uppercase tracking-[0.12em] px-2 py-1 rounded-sm border border-[#FFB800]/40 text-[#FFB800] bg-[#FFB800]/10 hover:bg-[#FFB800]/20 font-bold inline-flex items-center gap-1.5"
              >
                Ad-hoc · {adHocSymbol}
                <X size={10} />
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {bridgePass?.symbols?.length > 0 && (
            <span data-testid="bridge-indicator" className="text-[10px] font-mono text-[#22D3FF] bg-[#22D3FF]/10 border border-[#22D3FF]/30 px-2 py-1 rounded-md uppercase tracking-[0.14em]">
              Bridge · {bridgePass.symbols.length}
            </span>
          )}
          {/* Iter113.2 — Journal-context mirror chip. */}
          {lastJournalEntry ? (
            <button
              type="button"
              data-testid="scanner-journal-chip"
              onClick={() => navigate("/journal")}
              title="Open Decision Journal"
              className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0] border border-[#26262B] hover:border-[#3F3F47] px-2.5 py-1 rounded-md transition-colors"
            >
              <BookmarkCheck size={11} className="text-[#FFD700]" />
              <span>Last journaled</span>
              <span data-testid="scanner-journal-chip-symbol" className="font-bold text-white">{lastJournalEntry.symbol}</span>
              <span className="text-[#3F3F47]">·</span>
              <span
                data-testid="scanner-journal-chip-status"
                className="font-bold"
                style={{ color: JOURNAL_STATUS_COLORS[lastJournalEntry.status] || "#8A8A93" }}
              >
                {lastJournalEntry.status}
              </span>
              <span className="text-[#3F3F47]">·</span>
              <span data-testid="scanner-journal-chip-when" className="text-[#5F5F66] normal-case tracking-normal">
                {relativeTimeShort(lastJournalEntry.created_at)}
              </span>
            </button>
          ) : <span data-testid="scanner-journal-chip-empty" />}
          {/* Iter112 — Live-scan tri-state toggle (Phase 7D).
              Hidden entirely when REACT_APP_ENABLE_LIVE_SCAN is off. */}
          {LIVE_SCAN_ENABLED && (
            <div
              data-testid="live-scan-toggle-strip"
              className="flex items-center gap-1 px-1.5 py-1 rounded-md border border-[#26262B] bg-[#0A0A0E]"
              title="Live-scan tri-state: IDLE (manual) · LIVE (60s cadence) · PAUSED (frozen)"
            >
              {/* Heartbeat dot — pulsing cyan when LIVE healthy,
                  amber when paused / errored, dim grey when idle. */}
              <span
                data-testid="live-scan-heartbeat-dot"
                className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${
                  liveMode === LIVE_MODE_LIVE && !scanError ? "bg-[#22D3FF] animate-pulse"
                  : liveMode === LIVE_MODE_PAUSED ? "bg-[#FFB800]"
                  : "bg-[#3F3F47]"
                }`}
              />
              {[LIVE_MODE_IDLE, LIVE_MODE_LIVE, LIVE_MODE_PAUSED].map(m => (
                <button
                  key={m}
                  type="button"
                  data-testid={`live-scan-mode-${m.toLowerCase()}`}
                  onClick={() => {
                    setLiveMode(m);
                    if (m === LIVE_MODE_LIVE) setScanError(null);
                  }}
                  className={`px-2 py-0.5 rounded-sm text-[9px] font-mono font-bold uppercase tracking-[0.14em] transition-colors ${
                    liveMode === m
                      ? (m === LIVE_MODE_LIVE ? "bg-[#22D3FF]/20 text-[#22D3FF] border border-[#22D3FF]/40"
                        : m === LIVE_MODE_PAUSED ? "bg-[#FFB800]/15 text-[#FFB800] border border-[#FFB800]/40"
                        : "bg-[#26262B] text-white border border-[#3F3F47]")
                      : "text-[#5F5F66] hover:text-white border border-transparent"
                  }`}
                >
                  {m === LIVE_MODE_LIVE ? <span className="inline-flex items-center gap-1"><Activity size={9} />Live</span>
                  : m === LIVE_MODE_PAUSED ? <span className="inline-flex items-center gap-1"><Pause size={9} />Paused</span>
                  : <span className="inline-flex items-center gap-1"><Play size={9} />Idle</span>}
                </button>
              ))}
              <span data-testid="live-scan-tick-count" className="text-[8px] font-mono uppercase tracking-[0.18em] text-[#5F5F66] pl-1.5 pr-1">
                {liveMode === LIVE_MODE_LIVE && liveTickCount > 0 ? `Ticks · ${liveTickCount}` : "—"}
              </span>
            </div>
          )}
          <button
            type="button"
            data-testid="strategy-scanner-v2-watchlist-open"
            onClick={() => setWatchlistOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#FFD700]/40 bg-[#FFD700]/10 text-[#FFD700] text-[11px] font-bold uppercase tracking-[0.14em] hover:bg-[#FFD700]/15"
          >
            <Bookmark size={12} /> Manage Watchlist ({watchlistItems.length})
          </button>
          {/* Iter121.3 — Watchlist picker. Switching here changes the
              scanner's WATCHLIST source across the whole app. */}
          <div className="min-w-[180px] max-w-[240px]">
            <WatchlistPicker
              watchlists={watchlists}
              activeId={activeWatchlistId}
              onSwitch={switchActiveWatchlist}
              onManage={() => setShowManageWatchlists(true)}
              variant="expanded"
              testidPrefix="scanner-wl-picker"
            />
          </div>
          <button
            type="button"
            data-testid="strategy-scanner-v2-refresh"
            onClick={() => { lastScannedKeyRef.current = ""; runScan(resolvedSymbols); }}
            disabled={scanning || resolvedSymbols.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#26262B] bg-[#0A0A0E] text-[#E5E5E7] text-[11px] font-bold uppercase tracking-[0.14em] hover:border-[#00FF88]/60 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {scanning ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            {scanning ? "Scanning…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Iter112 — PAUSED banner.  Frozen-data signal so the operator
          knows the cards below are NOT updating in the background. */}
      {LIVE_SCAN_ENABLED && liveMode === LIVE_MODE_PAUSED && (
        <div
          data-testid="live-scan-paused-overlay"
          className="mb-3 rounded-md border border-[#FFB800]/40 bg-[#FFB800]/10 px-4 py-2 flex items-center gap-3"
        >
          <Pause size={14} className="text-[#FFB800]" />
          <div className="flex-1">
            <div className="text-[11px] font-mono font-bold uppercase tracking-[0.16em] text-[#FFB800]">
              Live scan paused
            </div>
            <div className="text-[10px] font-mono text-[#8A8A93] mt-0.5">
              Cards below show the last fetched snapshot.  Click <span className="text-[#22D3FF]">Live</span> to resume the 60s cadence.
              {scanError ? " · Auto-paused after an error response." : ""}
            </div>
          </div>
          <button
            type="button"
            data-testid="live-scan-paused-resume"
            onClick={() => { setScanError(null); setLiveMode(LIVE_MODE_LIVE); }}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-sm border border-[#22D3FF]/40 bg-[#22D3FF]/10 text-[#22D3FF] text-[10px] font-mono font-bold uppercase tracking-[0.14em] hover:bg-[#22D3FF]/15"
          >
            <Play size={10} /> Resume
          </button>
        </div>
      )}

      {/* Hero KPI strip */}
      <div data-testid="hero-kpi-strip" className="grid grid-cols-2 md:grid-cols-7 gap-2 mb-4">
        <DataChip label="Symbols" value={kpi.totalSymbols} accent="#FFFFFF" testid="kpi-symbols" />
        <DataChip label="ENTER" value={kpi.ENTER || 0} accent="#00FF88" testid="kpi-enter" />
        <DataChip label="WATCH" value={kpi.WATCH || 0} accent="#FFB800" testid="kpi-watch" />
        <DataChip label="NO_TRADE" value={kpi.NO_TRADE || 0} accent="#8A8A93" testid="kpi-no-trade" />
        <DataChip label="BLOCKED" value={kpi.BLOCKED || 0} accent="#FF3366" testid="kpi-blocked" />
        <DataChip label="CTX MISS" value={kpi.CONTEXT_MISSING || 0} accent="#FFB800" testid="kpi-ctx-miss" />
        <DataChip
          label="Last scan"
          value={lastScanAt ? new Date(lastScanAt).toLocaleTimeString() : (scanning ? "…" : "—")}
          accent="#22D3FF"
          testid="kpi-last-scan"
        />
      </div>

      {/* Iter115.6 — context-build telemetry strip.  Shows how many
          submitted symbols actually got a Market Snapshot + WES
          context built (= analyse-able) vs how many fell through
          (= 100 % CTX MISS in every module).  Tooltip on the
          "No context" chip lists each failed symbol verbatim from
          the backend warnings array. */}
      {resolvedSymbols.length > 0 && (
        <div data-testid="ctx-build-telemetry" className="flex items-center gap-2 flex-wrap text-[10px] font-mono uppercase tracking-[0.14em] mb-4">
          <span className="text-[#5F5F66]">Context build:</span>
          <span
            data-testid="ctx-build-built"
            title={`Symbols with a Market Snapshot AND/OR WES context built — eligible for per-module evaluation. Backend hard cap: 25 symbols per scan.`}
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border border-[#00FF88]/40 text-[#00FF88] bg-[#00FF88]/5 cursor-help"
          >
            <span className="text-[#8A8A93] font-normal normal-case tracking-normal">built</span>
            <span className="font-bold text-white">{Math.max(0, resolvedSymbols.length - buildWarnings.length)}</span>
            <span className="text-[#3F3F47]">/ {resolvedSymbols.length}</span>
          </span>
          {buildWarnings.length > 0 && (
            <span
              data-testid="ctx-build-failed"
              title={`Symbols with NO snapshot AND NO WES context — every per-module row for these will be CTX MISS regardless of strategy doctrine.\n\n${buildWarnings.slice(0, 50).join("\n")}${buildWarnings.length > 50 ? `\n(+ ${buildWarnings.length - 50} more…)` : ""}`}
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border border-[#FF3366]/40 text-[#FF3366] bg-[#FF3366]/5 cursor-help"
            >
              <span className="text-[#8A8A93] font-normal normal-case tracking-normal">failed</span>
              <span className="font-bold text-white">{buildWarnings.length}</span>
              <span className="text-[#3F3F47]">· hover for list</span>
            </span>
          )}
          {resolvedSymbols.length > 25 && (
            <span
              data-testid="ctx-build-cap-notice"
              title="The internal Market Snapshot + WES context builders both hard-cap at 25 symbols per scan.  When more than 25 are submitted, the builders reject the request and EVERY symbol ends up with no context — yielding 100 % CTX MISS.  Use Market Scanner pin-mode to narrow your set to ≤ 25 before sending."
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border border-[#FFB800]/40 text-[#FFB800] bg-[#FFB800]/5 cursor-help"
            >
              <span className="font-bold">25-symbol cap</span>
              <span className="text-[#8A8A93] font-normal normal-case tracking-normal">· you submitted {resolvedSymbols.length}</span>
            </span>
          )}
          {adHocSymbol && (
            <span
              data-testid="ctx-build-adhoc-mode"
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border border-[#22D3FF]/40 text-[#22D3FF] bg-[#22D3FF]/5"
            >
              <span className="font-bold">ad-hoc mode</span>
              <span className="text-[#8A8A93] font-normal normal-case tracking-normal">· only {adHocSymbol}</span>
            </span>
          )}
        </div>
      )}

      {/* Filter chip strip */}
      <div data-testid="filter-chip-strip" className="flex flex-wrap items-center gap-1.5 mb-4">
        {[
          ["actionable", "Actionable (ENTER · WATCH)"],
          ["enter", "ENTER only"],
          ["watch", "WATCH only"],
          ["blocked", "Blocked / CTX miss"],
          ["all", "All rows"],
        ].map(([k, label]) => (
          <button
            key={k}
            type="button"
            data-testid={`filter-${k}`}
            onClick={() => setStatusFilter(k)}
            className={`px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-[0.14em] border ${statusFilter === k ? "border-[#00FF88]/60 bg-[#00FF88]/10 text-[#00FF88]" : "border-[#26262B] bg-[#0A0A0E] text-[#8A8A93] hover:border-[#3F3F47] hover:text-[#E5E5E7]"}`}
          >{label}</button>
        ))}
        <button
          type="button"
          data-testid="filter-top-picks-only"
          onClick={() => setTopPicksOnly(v => !v)}
          className={`ml-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-[0.14em] border ${topPicksOnly ? "border-[#FFD700]/60 bg-[#FFD700]/10 text-[#FFD700]" : "border-[#26262B] bg-[#0A0A0E] text-[#8A8A93] hover:border-[#FFD700]/40 hover:text-[#FFD700]"}`}
        >
          <Star size={10} fill={topPicksOnly ? "currentColor" : "none"} /> Top picks only
        </button>
        <span className="ml-auto text-[10px] font-mono text-[#5F5F66]">
          {visibleRows.length} card{visibleRows.length === 1 ? "" : "s"} · context: {wesMeta?.engine_source_fingerprint ? wesMeta.engine_source_fingerprint.slice(0, 8) : "—"}
        </span>
      </div>

      {/* Error state */}
      {scanError && (
        <div data-testid="scan-error" className="mb-4 rounded-md border border-[#FF3366]/40 bg-[#FF3366]/10 px-4 py-3 text-[11px] font-mono text-[#FF8FAA]">
          {scanError}
        </div>
      )}

      {/* Empty state */}
      {!scanning && resolvedSymbols.length === 0 && (
        <div data-testid="empty-state" className="rounded-xl border border-[#26262B] bg-gradient-to-br from-[#0F0F14] to-[#08080A] py-16 text-center">
          <Radar size={36} className="mx-auto text-[#22D3FF] opacity-60 mb-3" />
          <div className="text-[14px] font-bold text-white mb-1">Awaiting watchlist symbols</div>
          <div className="text-[11px] font-mono text-[#8A8A93] mb-4">
            Add tickers to your watchlist, or run the Market Scanner to push opportunities here.
          </div>
          <button
            type="button"
            data-testid="empty-state-open-watchlist"
            onClick={() => setWatchlistOpen(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#FFD700]/10 border border-[#FFD700]/40 text-[#FFD700] text-[11px] font-bold uppercase tracking-[0.14em] hover:bg-[#FFD700]/15"
          >
            <Bookmark size={12} /> Open Watchlist
          </button>
        </div>
      )}

      {/* Loading skeletons */}
      {scanning && rows.length === 0 && (
        <div data-testid="loading-skeleton" className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-[560px] rounded-xl border border-[#26262B] bg-gradient-to-br from-[#0F0F14] to-[#08080A] animate-pulse" />
          ))}
        </div>
      )}

      {/* Cards grid */}
      {visibleRows.length > 0 && (
        <div data-testid="trading-cards-grid" className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-5">
          {visibleRows.map((r, i) => {
            const sym = r.symbol;
            const meta = symbolMeta[sym] || {};
            const raw = r.raw_module_response || {};
            return (
              <TradingCard
                key={`${sym}-${r.module_id}-${i}`}
                symbol={sym}
                moduleId={r.module_id}
                row={r}
                raw={raw}
                spot={meta.spot}
                dte={meta.dte}
                ivr={meta.ivr}
                regime={meta.regime}
                wes={meta.wes}
                isTopPick={meta.topModule === r.module_id && ACTIONABLE_STATUSES.has(r.status)}
                onCopy={handleCopy}
                copied={copiedKey === `${sym}-${r.module_id}`}
              />
            );
          })}
        </div>
      )}

      {/* Iter119.7 — Smart empty state after filter. Replaces the
          weak "No rows match the current filter." stub with a
          context-aware diagnostic: counts every row by status,
          tailors the headline + hint to the active filter +
          top-picks toggle, and offers one-click CTAs to escape
          the dead-end (e.g. "All 30 rows are CTX_MISS → View
          CTX_MISS reasons"). All status pills are themselves
          one-click filter jumps. */}
      {!scanning && rows.length > 0 && visibleRows.length === 0 && (() => {
        const counts = { ENTER: 0, WATCH: 0, NO_TRADE: 0, BLOCKED: 0, CONTEXT_MISSING: 0, ERROR: 0 };
        rows.forEach(r => { counts[r.status] = (counts[r.status] || 0) + 1; });
        const actionable = counts.ENTER + counts.WATCH;
        const blockedAll = counts.BLOCKED + counts.ERROR;

        // Diagnose the primary reason for the empty result + the
        // most useful CTA to escape it. Top-picks toggle wins
        // priority — it's the most common "hidden actionable" trap.
        let headline, hint, primaryCta = null;
        if (topPicksOnly && actionable > 0) {
          headline = `${actionable} actionable trade${actionable === 1 ? "" : "s"} hidden by Top Picks Only`;
          hint = `The Top Picks filter keeps only the highest-conviction module per symbol. Disable it to see the rest.`;
          primaryCta = { label: "Disable Top Picks Only", testid: "no-rows-cta-toppicks", icon: Star,
                          onClick: () => setTopPicksOnly(false) };
        } else if (statusFilter === "actionable") {
          if (counts.CONTEXT_MISSING === rows.length) {
            headline = "Snapshot has zero usable context";
            hint = `Every symbol failed the WES + market-snapshot build — every per-module row is CTX_MISS. Refresh, run again later, or inspect each symbol's failure reason.`;
            primaryCta = { label: "View CTX_MISS reasons", testid: "no-rows-cta-blocked",
                            onClick: () => setStatusFilter("blocked") };
          } else {
            headline = "No ENTER / WATCH trades in this snapshot";
            hint = `Engine evaluated ${rows.length} module-runs and rejected all of them. Switch to "All rows" or "Blocked / CTX miss" to inspect why.`;
            primaryCta = { label: "Show all rows", testid: "no-rows-cta-all",
                            onClick: () => setStatusFilter("all") };
          }
        } else if (statusFilter === "enter" && counts.WATCH > 0) {
          headline = `No ENTER trades, but ${counts.WATCH} WATCH-grade trade${counts.WATCH === 1 ? "" : "s"} found`;
          hint = `Switch to "WATCH only" to see the next-best opportunities.`;
          primaryCta = { label: "Show WATCH only", testid: "no-rows-cta-watch",
                          onClick: () => setStatusFilter("watch") };
        } else if (statusFilter === "watch" && counts.ENTER > 0) {
          headline = `No WATCH trades, but ${counts.ENTER} ENTER-grade trade${counts.ENTER === 1 ? "" : "s"} found`;
          hint = `Switch to "ENTER only" to see the highest-conviction picks.`;
          primaryCta = { label: "Show ENTER only", testid: "no-rows-cta-enter",
                          onClick: () => setStatusFilter("enter") };
        } else if (statusFilter === "blocked") {
          headline = "Nothing blocked — every row passed gating";
          hint = `${counts.NO_TRADE + actionable} module-run${(counts.NO_TRADE + actionable) === 1 ? "" : "s"} passed the gating doctrine. Switch back to a less-restrictive filter to see them.`;
          primaryCta = actionable > 0
            ? { label: "Show actionable", testid: "no-rows-cta-actionable", onClick: () => setStatusFilter("actionable") }
            : { label: "Show all rows", testid: "no-rows-cta-all", onClick: () => setStatusFilter("all") };
        } else if (statusFilter === "all") {
          // All filter hides CTX_MISS — if visible is 0, all rows are CTX_MISS.
          headline = `All ${counts.CONTEXT_MISSING} rows are CTX_MISS`;
          hint = `Every per-module evaluation lacked WES + market-snapshot context. Switch to "Blocked / CTX miss" to inspect each symbol's failure reason.`;
          primaryCta = { label: "View CTX_MISS reasons", testid: "no-rows-cta-blocked",
                          onClick: () => setStatusFilter("blocked") };
        } else {
          // Fallback (e.g. ENTER filter with 0 ENTER and 0 WATCH).
          headline = `No ${statusFilter.toUpperCase()} trades in this snapshot`;
          hint = `Engine rejected ${rows.length} module-run${rows.length === 1 ? "" : "s"}. Try "All rows" or "Blocked / CTX miss" to see why.`;
          primaryCta = { label: "Show all rows", testid: "no-rows-cta-all",
                          onClick: () => setStatusFilter("all") };
        }

        // Per-status pills — each clickable to jump to a relevant filter.
        const pills = [
          { count: counts.ENTER,            label: "ENTER",    filterKey: "enter",   tone: "green" },
          { count: counts.WATCH,            label: "WATCH",    filterKey: "watch",   tone: "cyan" },
          { count: counts.NO_TRADE,         label: "NO_TRADE", filterKey: "all",     tone: "grey" },
          { count: blockedAll,              label: "BLOCKED",  filterKey: "blocked", tone: "red" },
          { count: counts.CONTEXT_MISSING,  label: "CTX_MISS", filterKey: "blocked", tone: "amber" },
        ].filter(p => p.count > 0);

        const toneClasses = {
          green: "border-[#00FF88]/40 text-[#00FF88] bg-[#00FF88]/5 hover:bg-[#00FF88]/15",
          cyan:  "border-[#22D3FF]/40 text-[#22D3FF] bg-[#22D3FF]/5 hover:bg-[#22D3FF]/15",
          amber: "border-[#FFB800]/40 text-[#FFB800] bg-[#FFB800]/5 hover:bg-[#FFB800]/15",
          red:   "border-[#FF3366]/40 text-[#FF3366] bg-[#FF3366]/5 hover:bg-[#FF3366]/15",
          grey:  "border-[#3F3F47] text-[#8A8A93] bg-[#26262B]/30 hover:bg-[#26262B]/60 hover:text-white",
        };

        const PrimaryIcon = primaryCta?.icon;
        return (
          <div data-testid="no-rows-after-filter"
               className="rounded-xl border border-[#26262B] bg-gradient-to-br from-[#0F0F14] to-[#08080A] py-10 px-6">
            <div className="flex flex-col items-center text-center max-w-xl mx-auto">
              <SearchX size={36} className="text-[#22D3FF] opacity-60 mb-3" />
              <div data-testid="no-rows-headline" className="text-[14px] font-bold text-white mb-1.5">
                {headline}
              </div>
              <div data-testid="no-rows-hint" className="text-[11px] font-mono text-[#8A8A93] mb-4 leading-relaxed">
                {hint}
              </div>
              {pills.length > 0 && (
                <div data-testid="no-rows-status-strip"
                     className="flex flex-wrap items-center justify-center gap-1.5 mb-5">
                  {pills.map(p => (
                    <button
                      key={p.label}
                      type="button"
                      data-testid={`no-rows-status-${p.label}`}
                      onClick={() => setStatusFilter(p.filterKey)}
                      title={`Jump to ${p.label === "NO_TRADE" ? "All rows" : p.label === "CTX_MISS" || p.label === "BLOCKED" ? "Blocked / CTX miss" : `${p.label} only`}`}
                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-[0.14em] transition-colors ${toneClasses[p.tone]}`}
                    >
                      <span className="font-bold text-white">{p.count}</span>
                      <span className="font-normal normal-case tracking-normal">{p.label}</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap justify-center">
                {primaryCta && (
                  <button
                    type="button"
                    data-testid={primaryCta.testid}
                    onClick={primaryCta.onClick}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#22D3FF]/10 border border-[#22D3FF]/40 text-[#22D3FF] text-[11px] font-bold uppercase tracking-[0.14em] hover:bg-[#22D3FF]/20"
                  >
                    {PrimaryIcon && <PrimaryIcon size={12} />}
                    {primaryCta.label}
                  </button>
                )}
                {/* Always-visible "Refresh" — operator may want a
                    fresh snapshot regardless of the diagnostic above. */}
                <button
                  type="button"
                  data-testid="no-rows-cta-refresh"
                  onClick={() => { lastScannedKeyRef.current = ""; runScan(resolvedSymbols); }}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#26262B] border border-[#3F3F47] text-[#E5E5E7] text-[11px] font-bold uppercase tracking-[0.14em] hover:bg-[#3F3F47]"
                >
                  <RefreshCw size={12} /> Refresh scan
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Footer provenance + safety */}
      <div className="mt-8 pt-4 border-t border-[#26262B]">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[9px] font-mono uppercase tracking-[0.16em] text-[#3F3F47]">
          <span>Read-only decision cockpit</span>
          <span>·</span>
          <span>No auto-execution</span>
          <span>·</span>
          <span>No broker order placement (today)</span>
          <span>·</span>
          <span>No journal write</span>
          <span>·</span>
          <span>Human decides</span>
          {snapshotMeta?.snapshot_id && (
            <>
              <span>·</span>
              <span>Snapshot {snapshotMeta.snapshot_id.slice(0, 8)}</span>
            </>
          )}
        </div>
      </div>

      <WatchlistManager
        open={watchlistOpen}
        onClose={() => setWatchlistOpen(false)}
        items={watchlistItems}
        addSymbol={handleAdd}
        removeSymbol={handleRemove}
        addingSymbol={addingSymbol}
        isAdmin={isAdmin}
      />

      {/* Iter121.3 — Manage watchlists modal for the scanner. */}
      {showManageWatchlists && (
        <ManageWatchlistsModal
          watchlists={watchlists}
          activeId={activeWatchlistId}
          onClose={() => setShowManageWatchlists(false)}
          onRefresh={() => Promise.all([refetchWatchlists(activeWatchlistId), loadWatchlist()])}
          onSwitchActive={switchActiveWatchlist}
          testidPrefix="scanner-wl-manage"
        />
      )}

      {/* Iter115.7 — Gating Doctrine reference modal. */}
      <GatingDoctrineModal open={doctrineOpen} onClose={() => setDoctrineOpen(false)} />
    </div>
  );
}

// Iter115.7 — Static, evaluator-mirrored gating doctrine.  Each
// module's "Hard-fail order" below is copied verbatim from the
// docstrings in the corresponding `evaluator.py` (BPCS / BCCS /
// Iron Condor / CFM-Shares / CFM-LEAPS) so the UI and the engine
// never drift out of sync.  When you read "NO_EDGE" on a card,
// it means the module reached the bottom of its hard-fail list
// without finding a candidate that passes every gate.
const STRATEGY_DOCTRINE = [
  {
    id: "bpcs",
    title: "Bull Put Credit Spread (BPCS)",
    accent: "#00FF88",
    summary: "Sell a put spread for credit when WES is bullish-leaning at value support.",
    gates: [
      { label: "Wheel ITM actionable",          verdict: "BLOCKED", reason: "WHEEL_ACTIONABLE",  text: "An existing Wheel position on this symbol has an ITM CC that needs management — never open a new short-put spread on top." },
      { label: "Bar not closed",                verdict: "BLOCKED", reason: "BAR_NOT_CLOSED",    text: "The most recent daily bar hasn't settled yet — wait for the close so WES is anchored." },
      { label: "WES data stale",                verdict: "BLOCKED", reason: "DATA_STALE",        text: "WES context is older than the staleness threshold — refresh before trusting the value-area read." },
      { label: "Event risk active",             verdict: "BLOCKED", reason: "EVENT_RISK",        text: "Earnings or a binary event is in the DTE window — credit spreads through earnings are forbidden." },
      { label: "WES-P below threshold (< 6)",   verdict: "NO_TRADE", reason: "NO_EDGE",           text: "WES-P (put-side strength) is too weak — value-support thesis is not present." },
      { label: "ΔVAL < 0 and worsening",        verdict: "NO_TRADE", reason: "NO_EDGE",           text: "Spot is below VAL and the gap is widening — the put short would be naked into a breakdown." },
      { label: "No viable candidate strike",    verdict: "NO_TRADE", reason: "NO_EDGE",           text: "No strike on the chain meets the liquidity / credit-to-width / max-risk / pressing filters." },
      { label: "Liquidity insufficient",        verdict: "NO_TRADE", reason: "LIQUIDITY_FAIL",    text: "Bid-ask too wide or open interest too low to execute reliably." },
      { label: "Credit / width below floor",    verdict: "NO_TRADE", reason: "CREDIT_TOO_LOW",    text: "Net credit per dollar of width is below the doctrine's premium-collection floor." },
      { label: "Max-risk over portfolio limit", verdict: "NO_TRADE", reason: "RISK_LIMIT_EXCEEDED", text: "Sizing this spread would put account risk over the configured cap." },
    ],
    pass: "Best survivor scored ≥ 60 → ENTER · ≥ 35 → WATCH · else NO_EDGE.",
  },
  {
    id: "bccs",
    title: "Bear Call Credit Spread (BCCS)",
    accent: "#FF3366",
    summary: "Sell a call spread for credit when WES is bearish-leaning at value resistance.",
    gates: [
      { label: "Wheel ITM actionable",            verdict: "BLOCKED", reason: "WHEEL_ACTIONABLE",  text: "Existing Wheel position on this symbol has an ITM CC needing management — don't stack a short-call spread." },
      { label: "Bar not closed",                  verdict: "BLOCKED", reason: "BAR_NOT_CLOSED",    text: "Wait for the current bar to settle before reading WES." },
      { label: "WES data stale",                  verdict: "BLOCKED", reason: "DATA_STALE",        text: "WES context is past the staleness window — refresh first." },
      { label: "Event risk active",               verdict: "BLOCKED", reason: "EVENT_RISK",        text: "Earnings / binary event inside the DTE window — credit spreads through earnings are forbidden." },
      { label: "Accepted bullish breakout",       verdict: "NO_TRADE", reason: "NO_EDGE",           text: "Spot has accepted above VAH on closing basis — the short-call thesis is invalidated." },
      { label: "WES-C below threshold (< 6)",     verdict: "NO_TRADE", reason: "NO_EDGE",           text: "WES-C (call-side strength) is too weak — no resistance thesis present." },
      { label: "ΔVAH > 0 and strengthening",      verdict: "NO_TRADE", reason: "NO_EDGE",           text: "Spot is above VAH and gap widening — selling a call short is fighting the move." },
      { label: "Chain empty / DTE out of range",  verdict: "BLOCKED", reason: "LIQUIDITY_FAIL",    text: "No option chain returned or expirations don't fit the DTE band." },
      { label: "No viable candidate / filters",   verdict: "NO_TRADE", reason: "NO_EDGE / CREDIT_TOO_LOW / RISK_LIMIT_EXCEEDED", text: "No strike passes liquidity → credit/width → max-risk → pressing checks." },
    ],
    pass: "Best survivor scored ≥ 60 → ENTER · ≥ 35 → WATCH · else NO_EDGE.",
  },
  {
    id: "iron_condor",
    title: "Iron Condor (IC)",
    accent: "#22D3FF",
    summary: "Sell BOTH a put spread and a call spread when spot is mean-reverting around POC.",
    gates: [
      { label: "Wheel ITM actionable",                       verdict: "BLOCKED", reason: "WHEEL_ACTIONABLE", text: "Wheel position requires action — don't add range exposure on top." },
      { label: "Bar not closed",                             verdict: "BLOCKED", reason: "BAR_NOT_CLOSED",   text: "Wait for bar close — range thesis depends on settled VAH/VAL." },
      { label: "WES data stale",                             verdict: "BLOCKED", reason: "DATA_STALE",       text: "Refresh WES before assessing the range." },
      { label: "Event risk active",                          verdict: "BLOCKED", reason: "EVENT_RISK",       text: "Earnings inside DTE — range trades can't survive a gap." },
      { label: "Accepted breakout above VAH OR breakdown below VAL", verdict: "NO_TRADE", reason: "NO_EDGE", text: "Spot has accepted outside the value area — the symbol is no longer mean-reverting." },
      { label: "Spot outside value area / not near POC",     verdict: "NO_TRADE", reason: "NO_EDGE",         text: "Iron Condor needs spot inside [VAL, VAH] and reasonably near POC." },
      { label: "WES-P or WES-C directionally dominant",      verdict: "NO_TRADE", reason: "NO_EDGE",         text: "One side of WES strongly outweighs the other — directional spread is a better fit than a condor." },
      { label: "Weak range quality",                         verdict: "NO_TRADE", reason: "NO_EDGE",         text: "Realised range over the lookback is too narrow or too wide for healthy condor premium." },
      { label: "Empty / missing chain",                      verdict: "BLOCKED", reason: "LIQUIDITY_FAIL",   text: "No option chain for the symbol — can't price the four legs." },
      { label: "Per-leg liquidity → credit/width → max-risk", verdict: "NO_TRADE", reason: "LIQUIDITY_FAIL / CREDIT_TOO_LOW / RISK_LIMIT_EXCEEDED", text: "No 4-leg combo passes the liquidity, credit-to-width, max-loss and pressing checks." },
    ],
    pass: "Best survivor scored ≥ 60 → ENTER · ≥ 35 → WATCH · else NO_EDGE.",
  },
  {
    id: "cfm_shares",
    title: "Covered Call / Wheel Shares (CFM-Shares)",
    accent: "#FFB800",
    summary: "Sell a CC against existing or proposed 100-share lots; manage open CC campaigns.",
    gates: [
      { label: "WES data stale or missing",          verdict: "BLOCKED",  reason: "DATA_STALE",          text: "Can't size delta band without fresh WES." },
      { label: "No options chain available",         verdict: "BLOCKED",  reason: "LIQUIDITY_FAIL",      text: "Underlier has no listed options — Wheel doctrine cannot apply." },
      { label: "Existing CC inside management band", verdict: "MANAGEMENT", reason: "BUYBACK / DEFEND / ROLL_OUT_UP / ALLOW_ASSIGNMENT", text: "Open short-call needs human-decided management; evaluator does NOT auto-roll." },
      { label: "Regime / delta-band mismatch",       verdict: "NO_TRADE", reason: "NO_EDGE",             text: "Current regime (e.g. BEARISH_BELOW_VALUE) maps to a delta band with no acceptable strike on the chain." },
      { label: "Premium cushion < floor",            verdict: "NO_TRADE", reason: "NO_EDGE",             text: "Premium as % of spot is below the doctrine floor — extrinsic ROI insufficient." },
      { label: "Assignment math unfavourable",       verdict: "NO_TRADE", reason: "NO_EDGE",             text: "If called, exit price would print a loss vs cost basis — don't accept assignment risk." },
    ],
    pass: "Acceptable strike + premium + assignment math → ENTER / WATCH per score.",
  },
  {
    id: "cfm_leaps",
    title: "Poor-Man's Covered Call / Wheel LEAPS (CFM-LEAPS)",
    accent: "#A78BFA",
    summary: "Sell short-dated CCs against a deep-ITM LEAP long call as synthetic stock replacement.",
    gates: [
      { label: "WES data stale or missing",          verdict: "BLOCKED",  reason: "DATA_STALE",          text: "Can't size delta band without fresh WES." },
      { label: "No LEAP candidate ≥ 0.80 delta",     verdict: "NO_TRADE", reason: "NO_EDGE",             text: "Doctrine requires the long leg to behave like stock — 80-delta floor preserves intrinsic." },
      { label: "Short CC strike inside LEAP strike", verdict: "BLOCKED",  reason: "INVALID_STRUCTURE",   text: "Short call must be strictly above the LEAP long strike to avoid assignment-loss." },
      { label: "Short CC chain liquidity fail",      verdict: "BLOCKED",  reason: "LIQUIDITY_FAIL",      text: "Short-leg bid-ask too wide or OI too low." },
      { label: "Net debit cushion too thin",         verdict: "NO_TRADE", reason: "NO_EDGE",             text: "Premium collected over net LEAP cost is below the doctrine floor — synthetic Wheel has no edge." },
    ],
    pass: "LEAP delta ≥ 0.80 + acceptable short-CC strike + premium cushion → ENTER / WATCH.",
  },
];

// Iter115.8 — given a card's reason_codes and module_id, find
// the matching doctrine row(s) so the operator can read EXACTLY
// why the engine returned that verdict.
function findMatchingDoctrineRows(moduleId, reasonCodes) {
  // Iter115.8.1 — engine returns module_id in UPPER_CASE
  // (`CFM_SHARES`, `BPCS`); doctrine ids are lowercase.  Normalize.
  const modIdLower = String(moduleId || "").toLowerCase();
  // Bridge legacy aliases:
  const aliased = modIdLower === "cfm_cc_shares" ? "cfm_shares"
                : modIdLower === "cfm_cc_leaps"  ? "cfm_leaps"
                : modIdLower;
  const mod = STRATEGY_DOCTRINE.find(m => m.id === aliased);
  if (!mod || !Array.isArray(reasonCodes) || reasonCodes.length === 0) return [];
  const out = [];
  reasonCodes.forEach(rc => {
    const codeUpper = String(rc).toUpperCase();
    mod.gates.forEach((g, idx) => {
      const segs = String(g.reason || "").toUpperCase().split(/\s*\/\s*/);
      if (segs.includes(codeUpper)) {
        if (!out.find(o => o.gate === g)) {
          out.push({ priority: idx + 1, gate: g, code: codeUpper, module: mod });
        }
      }
    });
  });
  return out;
}

function WhyVerdictTrigger({ symbol, moduleId, status, decision, reasonCodes }) {
  const [open, setOpen] = React.useState(false);
  const verdictLabel = decision || status || "—";
  const isPositive = verdictLabel === "ENTER";
  const matched = React.useMemo(
    () => findMatchingDoctrineRows(moduleId, reasonCodes),
    [moduleId, reasonCodes],
  );
  const mod = STRATEGY_DOCTRINE.find(m => {
    const lower = String(moduleId || "").toLowerCase();
    const aliased = lower === "cfm_cc_shares" ? "cfm_shares"
                  : lower === "cfm_cc_leaps"  ? "cfm_leaps"
                  : lower;
    return m.id === aliased;
  });
  return (
    <div className="relative">
      <button
        type="button"
        data-testid={`why-verdict-${symbol}-${moduleId}`}
        onClick={() => setOpen(o => !o)}
        title="See which doctrine gate(s) drove this verdict."
        className={`inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-sm border ${isPositive
          ? "border-[#00FF88]/40 text-[#00FF88] bg-[#00FF88]/5 hover:bg-[#00FF88]/15"
          : "border-[#22D3FF]/40 text-[#22D3FF] bg-[#22D3FF]/5 hover:bg-[#22D3FF]/15"}`}
      >
        <span>?</span>
        <span>Why {verdictLabel}</span>
      </button>
      {open && (
        <div
          data-testid={`why-verdict-pop-${symbol}-${moduleId}`}
          className="absolute right-0 top-[110%] z-30 w-[320px] rounded-md border border-[#26262B] bg-[#0F0F14] shadow-2xl p-3 text-[11px]"
        >
          <div className="flex items-center justify-between gap-2 mb-2">
            <div>
              <div className="text-[9px] font-mono uppercase tracking-[0.14em] text-[#5F5F66]">{mod?.title || moduleId}</div>
              <div className="text-white font-bold">{symbol} · {verdictLabel}</div>
            </div>
            <button
              type="button"
              data-testid={`why-verdict-close-${symbol}-${moduleId}`}
              onClick={() => setOpen(false)}
              className="text-[#8A8A93] hover:text-white text-xs"
            >×</button>
          </div>
          {reasonCodes.length === 0 ? (
            <p className="text-[#8A8A93] italic">
              No reason codes returned for this row — likely a positive verdict where the score-driven pass rule promoted the survivor.
            </p>
          ) : matched.length === 0 ? (
            <div className="space-y-2">
              <p className="text-[#8A8A93]">
                Engine returned: <span className="text-white font-mono">{reasonCodes.join(", ")}</span>
              </p>
              {/* Iter115.8.1 — for ENTER/WATCH/ALLOW_ASSIGNMENT
                  verdicts the reason codes are typically POSITIVE
                  signals (SUPPORT_RECLAIM, ASSIGNMENT_ACCEPTABLE,
                  POC_BALANCE…) — the gate list only documents the
                  NEGATIVE blocks.  Communicate that explicitly so
                  the operator doesn't read it as a doctrine miss. */}
              {(verdictLabel === "ENTER" || verdictLabel === "WATCH" || verdictLabel === "ALLOW_ASSIGNMENT") ? (
                <p className="text-[#00FF88] text-[10px]">
                  ✓ This card passed every hard-fail gate.  The codes above are positive signals describing <span className="font-bold">why</span> the engine ranked it highly (regime alignment, assignment math, premium cushion, etc.).
                </p>
              ) : (
                <p className="text-[#5F5F66] text-[10px]">
                  These codes are not documented in the doctrine modal yet.  Open the full doctrine to scan the module&apos;s gate list.
                </p>
              )}
            </div>
          ) : (
            <ol className="space-y-2">
              {matched.slice(0, 4).map(({ priority, gate, code }, i) => (
                <li key={i} className="border-l-2 pl-2" style={{ borderColor: mod?.accent || "#22D3FF" }}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[9px] font-mono text-[#5F5F66]">gate #{priority}</span>
                    <span className="text-[9px] font-mono uppercase tracking-[0.10em] px-1.5 py-0.5 rounded-sm border border-[#FFB800]/40 text-[#FFB800] bg-[#FFB800]/10">{code}</span>
                  </div>
                  <div className="text-white font-bold text-[12px] mt-0.5">{gate.label}</div>
                  <div className="text-[#8A8A93] text-[11px]">{gate.text}</div>
                </li>
              ))}
            </ol>
          )}
          <div className="mt-3 pt-2 border-t border-[#26262B] text-[9px] font-mono uppercase tracking-[0.14em] text-[#5F5F66]">
            For the full doctrine, click the &quot;? Gating Doctrine&quot; button in the page header.
          </div>
        </div>
      )}
    </div>
  );
}

function GatingDoctrineModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      data-testid="gating-doctrine-overlay"
      onClick={onClose}
      className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div
        data-testid="gating-doctrine-modal"
        onClick={e => e.stopPropagation()}
        className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl bg-gradient-to-br from-[#0F0F14] to-[#08080A] border border-[#22D3FF]/30 shadow-2xl"
      >
        <div className="sticky top-0 flex items-center justify-between px-6 py-4 border-b border-[#26262B] bg-[#0F0F14]/95 backdrop-blur-sm">
          <div>
            <h3 className="text-base font-bold text-white tracking-tight">Strategy Scanner — Gating Doctrine</h3>
            <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#5F5F66] mt-1">
              Every gate below is mirrored from the evaluator&apos;s hard-fail order.  When a card reads NO_EDGE / BLOCKED, the strategy hit one of these gates.
            </p>
          </div>
          <button
            type="button"
            data-testid="gating-doctrine-close"
            onClick={onClose}
            className="text-[#8A8A93] hover:text-white"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6 text-[12px] leading-relaxed">
          {STRATEGY_DOCTRINE.map(mod => (
            <section
              key={mod.id}
              data-testid={`gating-doctrine-section-${mod.id}`}
              className="border border-[#26262B] rounded-lg overflow-hidden"
              style={{ borderColor: `${mod.accent}33` }}
            >
              <header
                className="px-4 py-3 border-b border-[#26262B] flex flex-wrap items-baseline justify-between gap-2"
                style={{ background: `${mod.accent}0D` }}
              >
                <div>
                  <h4 className="text-[13px] font-bold tracking-tight" style={{ color: mod.accent }}>{mod.title}</h4>
                  <p className="text-[11px] text-[#8A8A93] mt-0.5">{mod.summary}</p>
                </div>
                <span className="text-[9px] font-mono uppercase tracking-[0.14em] text-[#5F5F66]">module · {mod.id}</span>
              </header>
              <ol className="divide-y divide-[#1F1F26]">
                {mod.gates.map((g, i) => {
                  const isBlock = g.verdict.startsWith("BLOCKED");
                  const isMgmt  = g.verdict === "MANAGEMENT";
                  const pillCls = isBlock
                    ? "border-[#FF3366]/40 text-[#FF3366] bg-[#FF3366]/10"
                    : isMgmt
                    ? "border-[#22D3FF]/40 text-[#22D3FF] bg-[#22D3FF]/10"
                    : "border-[#FFB800]/40 text-[#FFB800] bg-[#FFB800]/10";
                  return (
                    <li key={i} className="px-4 py-3 flex flex-col sm:flex-row gap-2 sm:gap-4">
                      <div className="flex sm:flex-col sm:items-start gap-2 sm:gap-1 sm:min-w-[140px]">
                        <span className="text-[10px] font-mono text-[#5F5F66]">#{i + 1}</span>
                        <span className={`text-[9px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-sm border font-bold ${pillCls}`}>
                          {g.verdict}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="font-bold text-white">{g.label}</div>
                        <div className="text-[#8A8A93] text-[11px]">{g.text}</div>
                        <div className="mt-1 text-[9px] font-mono uppercase tracking-[0.14em] text-[#5F5F66]">
                          reason_code · {g.reason}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
              <footer className="px-4 py-2 border-t border-[#1F1F26] bg-[#08080A] text-[10px] font-mono text-[#8A8A93]">
                If no gate trips: {mod.pass}
              </footer>
            </section>
          ))}

          <div className="px-3 py-2 border border-[#22D3FF]/30 bg-[#22D3FF]/5 rounded-md text-[11px] text-[#22D3FF] font-mono">
            Reading a card: status pill in the top-right + per-gate reason inside the ACTION badge.  Hover the verdict pill for the doctrine row that fired (Iter115.7).
          </div>
        </div>
      </div>
    </div>
  );
}
